import type { HelpRouteRule, PageHelpContent } from './helpTypes';
import { hp } from './helpBuilders';
import { ROUTES } from './routes';

/** Guías ya existentes (texto detallado conservado). */
export const helpDashboard: PageHelpContent = {
  title: 'Indicadores (Dashboard)',
  intro:
    'Resumen operativo del negocio: ventas y compras del período, cobranzas y pagos, y estado de cuentas corrientes de clientes y proveedores.',
  sections: [
    {
      id: 'que-hace',
      title: 'Qué hace esta pantalla',
      body:
        'Consolida métricas y gráficos para que puedas comparar períodos y detectar desvíos rápidamente. Los datos se actualizan al cambiar el filtro de período.',
    },
    {
      id: 'pasos',
      title: 'Pasos recomendados',
      bullets: [
        'Elegí el período: mes actual, mes anterior o un mes/año personalizado.',
        'Revisá tarjetas de totales y el gráfico de evolución mensual.',
        'Usá los accesos desde las tarjetas (cuando correspondan) para ir al detalle de facturas con el rango de fechas del período seleccionado.',
      ],
    },
    {
      id: 'campos',
      title: 'Controles importantes',
      bullets: [
        'Filtro de período: define qué mes/año se usa para ventas, compras y derivados.',
        'Indicadores de cuenta corriente: reflejan saldos y vencimientos según la información del servidor.',
      ],
    },
    {
      id: 'errores',
      title: 'Si algo falla',
      bullets: [
        'Si aparece error al cargar, verificá conexión y volvé a intentar; el mensaje suele indicar el motivo devuelto por el servidor.',
        'Si un total no coincide con lo esperado, confirmá que el período seleccionado sea el correcto.',
      ],
    },
    {
      id: 'tips',
      title: 'Tips',
      bullets: [
        'Compará mes actual vs anterior cambiando el filtro para ver tendencias.',
        'Desde aquí podés saltar a listados filtrados sin rearmar fechas a mano.',
      ],
    },
  ],
  links: [{ label: 'Ir a facturas de venta', to: '/ventas/facturas' }],
};

export const helpFacturaVenta: PageHelpContent = {
  title: 'Factura de venta',
  intro:
    'Emisión y consulta de facturas de venta: cliente, ítems, totales, forma de pago y datos de entrega. Podés crear una factura nueva, cargar desde pedido/cotización o abrir una existente en modo ver o editar.',
  sections: [
    {
      id: 'que-hace',
      title: 'Qué hace esta pantalla',
      body:
        'Centraliza la cabecera del comprobante (cliente, fechas, condiciones) y el detalle de productos o servicios. El sistema calcula importes según precios, descuentos e IVA según corresponda.',
    },
    {
      id: 'pasos',
      title: 'Pasos recomendados',
      bullets: [
        'Seleccioná el cliente; completá datos obligatorios de cabecera (fecha, lista de precios, vendedor, etc. según tu proceso).',
        'Agregá líneas de detalle buscando artículos o cargando desde un pedido/cotización si llegaste con esos parámetros en la URL.',
        'Revisá totales, forma de pago y transporte antes de grabar.',
        'En modo solo lectura (ver) no se modifican datos; en editar ajustá lo necesario y guardá.',
      ],
    },
    {
      id: 'campos',
      title: 'Campos y conceptos clave',
      bullets: [
        'Cliente: determina condición fiscal, precios y datos de facturación.',
        'Forma de pago: debe ser coherente con la condición de venta (contado, cuenta corriente, etc.).',
        'Transporte / ubicación: útiles para logística y documentación.',
        'Detalle: cantidad, precio unitario, descuentos por línea; verificá moneda y cotización si operás en USD.',
        'Al agregar un artículo que no viene de un pedido o cotización, la unidad y el stock salen de UM Ref. Facturación. Si está vacía, se usa la unidad de medida base. El stock se muestra en esa unidad, con coma decimal. Las líneas traídas de un documento anterior conservan la unidad de ese documento.',
        'Si el artículo tiene equivalencias de UM, en la columna U/M un chip muestra la unidad actual y abre un menú con el resto. Queda seleccionada la UM del documento origen (cotización/pedido/pendiente); si no hay, la UM base. Al elegir otra se convierte el precio unitario (y el subtotal) a esa unidad; la cantidad no cambia. Sin equivalencia no se inventa factor.',
        'En consulta, Cantidad y UM muestran la unidad base del artículo. Si cargaste en otra UM con equivalencia, aparecen también Cant. Fac / UM Fac.',
      ],
    },
    {
      id: 'errores',
      title: 'Errores frecuentes',
      bullets: [
        'Cliente no seleccionado o incompleto: el sistema puede rechazar el guardado hasta completar datos mínimos.',
        'Artículo sin precio o cantidad inválida: revisá la línea antes de confirmar.',
        'Inconsistencias al traer pedido/cotización: verificá que el documento origen siga vigente y permita facturar.',
        'Sin equivalencia entre dos UM: el chip no cambia la línea; cargá la equivalencia en el maestro del artículo.',
      ],
    },
    {
      id: 'tips',
      title: 'Tips',
      bullets: [
        'Si abrís la pantalla con ?pedidoId= o ?cotizacionId= en la URL, el flujo puede precargar datos desde ese documento.',
        'Si el pedido origen tiene conversión (Cantidad B), aparece un aviso antes de precargar, al traer pendientes o al repetir el último pedido: se factura el remanente del origen.',
        'Desde seguimiento de ventas pendientes podés llegar con estado para aplicar cantidades sugeridas.',
        'Usá el menú de UM (chip con flecha) para revisar la misma línea en bolsas, kilos u otra unidad equivalente sin recalcular a mano.',
        'Al facturar desde pedido o cotización se reutiliza la UM y cantidad ingresadas en el origen (snapshot), no solo la UM base.',
      ],
    },
  ],
  links: [
    { label: 'Listado de facturas', to: '/ventas/facturas' },
    { label: 'Pedidos', to: '/ventas/pedidos' },
    { label: 'Cotizaciones', to: '/ventas/cotizaciones' },
  ],
};

export const helpNotaCreditoCompra: PageHelpContent = {
  title: 'Nueva nota de crédito de compra',
  intro:
    'Registro de una nota de crédito recibida del proveedor. Suele asociarse a una factura de compra origen para mantener coherencia de cantidades, precios e IVA.',
  sections: [
    {
      id: 'que-hace',
      title: 'Qué hace esta pantalla',
      body:
        'Permite cargar cabecera (proveedor, compañía, fechas, comprobante), ítems de producto y conceptos/gastos, impuestos adicionales y aplicar datos desde una factura de compra o compras pendientes.',
    },
    {
      id: 'pasos',
      title: 'Pasos recomendados',
      bullets: [
        'Si tenés la factura origen, usá “Buscar factura” o ingresá el número de formulario para traer proveedor, ítems y descuentos.',
        'Verificá compañía fiscal (ID compañía) y letra/tipo de comprobante.',
        'Ajustá cantidades o importes según lo acordado con el proveedor en la nota de crédito.',
        'Revisá separación entre ítems de producto (tipo C) y conceptos/gastos (tipo G) antes de guardar.',
        'Grabá cuando totales e IVA reflejen el comprobante físico/digital.',
      ],
    },
    {
      id: 'campos',
      title: 'Campos importantes',
      bullets: [
        'Factura origen: al seleccionarla se precargan proveedor, ubicación, forma de pago y líneas.',
        'Proveedor y ubicación de destino: impactan stock y contabilización según configuración.',
        'UM del ítem: se toma de la UM de compra del artículo/proveedor al cargar el producto; se envía con el renglón.',
        'Bonificaciones por cabecera y por ítem: influyen en costos e impuestos.',
        'Impuestos adicionales: agregá solo los que correspondan al comprobante real.',
      ],
    },
    {
      id: 'errores',
      title: 'Errores frecuentes',
      bullets: [
        'Número de factura origen inválido: debe existir en el sistema y ser del proveedor correcto.',
        'Mezclar mal productos y conceptos: revisá el tipo de línea (C vs G) frente al comprobante.',
        'Fechas de emisión / aplicación IVA incoherentes con la factura: pueden generar diferencias en reportes.',
      ],
    },
    {
      id: 'tips',
      title: 'Tips',
      bullets: [
        'Si venís desde “compras pendientes”, el estado de navegación puede precargar líneas vinculadas.',
        'Expandí acordeones de productos/conceptos para revisar todo antes de confirmar.',
      ],
    },
  ],
  links: [
    { label: 'Listado de notas de crédito (compras)', to: '/compras/notas-credito' },
    { label: 'Facturas de compra', to: '/compras/facturas' },
  ],
};

const helpMenuPrincipal = hp(
  'Menú principal',
  'Punto de entrada para elegir el módulo (Ventas, Compras, Inventario, Tesorería e Indicadores). Cada tarjeta lleva al submenú correspondiente.',
  {
    pasos: [
      'Revisá la descripción de cada módulo para ubicar la funcionalidad que necesitás.',
      'Hacé clic en la tarjeta del módulo para ver todas las opciones disponibles.',
      'Usá los chips de acceso rápido cuando quieras ir directo a un submenú sin pasar por la pantalla intermedia.',
    ],
    campos: [
      'Las tarjetas reflejan la configuración del menú lateral: si falta algo, puede deberse a permisos de usuario.',
    ],
    errores: ['Si no ves un módulo esperado, consultá permisos con el administrador.'],
    tips: ['Podés volver siempre aquí desde el logo o enlaces “Volver al menú principal”.'],
    links: [{ label: 'Indicadores', to: '/dashboard' }],
  }
);

const helpModuloVentas = hp(
  'Módulo Ventas',
  'Selector de funciones de ventas: cotizaciones, pedidos, movimientos internos, facturas, remitos, clientes, informes y más.',
  {
    pasos: [
      'Elegí la tarjeta del proceso que necesitás (por ejemplo Facturas, Pedidos o Movimientos internos).',
      'Usá “Volver al menú principal” si querés cambiar de módulo.',
    ],
    tips: ['El menú lateral repite estas mismas opciones para acceso rápido.'],
    links: [
      { label: 'Facturas', to: '/ventas/facturas' },
      { label: 'Movimientos internos', to: '/ventas/movimientos-internos' },
      { label: 'Clientes', to: '/ventas/clientes' },
    ],
  }
);

const helpModuloCompras = hp(
  'Módulo Compras',
  'Selector de funciones de compras: órdenes, remitos, facturas, movimientos internos, proveedores e informes.',
  {
    pasos: [
      'Seleccioná la operación (facturas de compra, órdenes, movimientos internos, cuentas por pagar, etc.).',
      'Desde aquí podés saltar a altas o listados según la tarjeta.',
    ],
    tips: ['El menú lateral repite estas mismas opciones para acceso rápido.'],
    links: [
      { label: 'Facturas de compra', to: '/compras/facturas' },
      { label: 'Movimientos internos', to: '/compras/movimientos-internos' },
      { label: 'Cuentas por pagar', to: '/compras/informes/cuentas-pagar' },
      { label: 'Proveedores', to: '/compras/proveedores' },
    ],
  }
);

const helpModuloInventario = hp(
  'Módulo Inventario',
  'Selector de stock, productos maestros, ajustes/transferencias e informes de inventario.',
  {
    pasos: [
      'Elegí Stock por depósito para ver existencias, o Productos para el maestro.',
      'Usá Transferencias para movimientos entre ubicaciones e Informes para el resto de reportes.',
    ],
    links: [
      { label: 'Stock por depósito', to: '/inventario/informes/stock-deposito' },
      { label: 'Productos', to: '/inventario/productos' },
      { label: 'Transferencias', to: '/inventario/transferencias' },
    ],
  }
);

const helpModuloTesoreria = hp(
  'Módulo Tesorería',
  'Selector de cobranzas, pagos, caja, bancos, conciliación e informes de tesorería.',
  {
    pasos: [
      'Elegí Recibos, Orden de pago, Movimientos de caja u otra opción según la tarea.',
      'Las tablas auxiliares (bancos, carteras, medios de cobro/pago) suelen estar en la misma sección.',
    ],
    links: [
      { label: 'Recibos', to: '/tesoreria/recibos' },
      { label: 'Planilla de caja', to: '/tesoreria/movimientos-caja' },
    ],
  }
);

const helpConsultaIa = (modulo: string): PageHelpContent =>
  hp(`Consulta IA (${modulo})`, `Asistente de consultas en lenguaje natural sobre datos del módulo ${modulo}.`, {
    queHace:
      'Permite preguntar con tus palabras sobre los datos del módulo. La respuesta sale del modelo de IA configurado para la empresa y de la información a la que llega tu usuario.',
    pasos: [
      'Si sos administrador y la empresa todavía no tiene IA, abrí el engranaje del título, elegí el proveedor, cargá la API key y guardá.',
      'Formulá preguntas concretas y usá los filtros de fecha para acotar el período.',
      'Revisá siempre la respuesta contra reportes oficiales antes de tomar una decisión.',
    ],
    campos: [
      'Engranaje: lo ven solo admin y superadmin. Ahí se elige proveedor, modelo, URL y API key de la empresa. Vale para Ventas, Compras, Inventario y Tesorería.',
      'Modelo vacío: se usa el sugerido del proveedor. La clave no se vuelve a mostrar; si la dejás vacía al guardar, se conserva la actual.',
      'Personalizado: para un proveedor que no está en la lista. La URL tiene que ser compatible con OpenAI y, si no termina en /v1, conviene pegarla completa hasta /chat/completions.',
      'Quitar configuración: borra la clave. Hasta cargar otra, las consultas avisan que la empresa no tiene IA configurada.',
    ],
    errores: [
      'Si dice que la empresa no tiene configurada la IA, un administrador tiene que cargar la clave con el engranaje.',
      'Si la IA no tiene acceso a un dato, puede deberse a permisos o a que la información no esté cargada.',
    ],
    tips: [
      'El botón de ayuda del engranaje explica el alta, el modelo y cómo quitar la clave.',
      'Usá fechas y filtros en la pregunta para acotar resultados.',
    ],
  });

/** Lista / formulario en subrutas no cubiertas por una guía más específica. */
const helpListadoTrabajoGenerico = hp(
  'Listados y operaciones',
  'Pantalla de trabajo con listados, filtros y acciones sobre registros (altas, edición y consulta según permisos).',
  {
    pasos: [
      'Usá filtros por fecha, estado o entidad para acotar resultados.',
      'Abrí una fila para ver detalle o editar cuando esté disponible.',
      'Para crear un registro nuevo buscá el botón “Nuevo” u opción equivalente.',
    ],
    campos: [
      'Las columnas dependen del tipo de documento o maestro: números, fechas, importes, referencias.',
    ],
    errores: ['Si no cargan datos, revisá sesión, permisos de perfil y filtros demasiado restrictivos.'],
  }
);

const helpMaestroCliente = hp(
  'Clientes',
  'Alta, edición y consulta de clientes: datos fiscales, condiciones de venta, direcciones y parámetros comerciales.',
  {
    pasos: [
      'En listado: buscá por nombre, código o CUIT.',
      'En alta/edición: completá obligatorios marcados o validados al guardar.',
      'Grabá antes de cambiar de pantalla.',
    ],
    campos: [
      'Condición de venta y lista de precios suelen definir comportamiento en facturación.',
      'CUIT/DNI y categoría fiscal deben coincidir con documentación oficial.',
    ],
    errores: ['Duplicidad de CUIT o código: el sistema puede rechazar el guardado.'],
    links: [{ label: 'Facturas', to: '/ventas/facturas' }],
  }
);

const helpPedidosCotizaciones = (tipo: string): PageHelpContent =>
  hp(tipo, `Gestión del circuito comercial de ${tipo.toLowerCase()}: cabecera, ítems, precios y estados hasta facturar o cerrar.`, {
    pasos: [
      'Seleccioná cliente y condiciones comerciales.',
      'Agregá líneas de productos con cantidades y precios.',
      'Cuando corresponda, generá factura o pedido siguiente desde las acciones disponibles.',
    ],
    campos: [
      'Estados del documento y fechas impactan la disponibilidad para facturar.',
      'Lista de precios: al elegir el cliente se toma la lista asignada; los precios de los artículos salen de esa lista (podés cambiarla en totales).',
      'UM de cada renglón: viene del detalle del documento; al editar se reenvía con el ítem.',
      ...(tipo === 'Pedidos' || tipo === 'Cotizaciones'
        ? [
            'Al agregar un artículo, la unidad y el stock del buscador salen de UM Ref. Pedido. Si está vacía, se usa la unidad de medida base. El stock se muestra en esa unidad, con coma decimal.',
            'Si el artículo tiene equivalencias, en U/M un chip abre un menú con las unidades. Queda seleccionada la del renglón (o la base si faltaba); al elegir otra se convierte el precio unitario (y el subtotal); la cantidad no cambia.',
            'En el detalle, Cantidad y UM son la unidad base. Si ingresaste otra UM con equivalencia, se muestran también Cant. Ped / UM Ped.',
          ]
        : []),
    ],
    tips: [
      'Podés llegar desde URLs con ID para editar o solo ver según la ruta.',
      ...(tipo === 'Pedidos'
        ? [
            'Si el pedido tiene conversión hecha (Cantidad B), al facturar el sistema pide confirmación: se factura el remanente del origen.',
          ]
        : []),
      ...(tipo === 'Pedidos' || tipo === 'Cotizaciones'
        ? [
            'El menú de UM sirve para revisar el mismo renglón en otra unidad equivalente sin recalcular a mano.',
            'Las columnas Cant. Ped / UM Ped solo aparecen cuando la UM ingresada difiere de la base.',
          ]
        : []),
    ],
  });

const helpPickingMasivo = hp(
  'Picking masivo',
  'Preparación de pedidos de venta enviados a picking: se leen los artículos con lector o teclado hasta completar el pedido y luego queda listo para facturar.',
  {
    pasos: [
      'Desde Pedidos, usá “Enviar a picking” en el menú ⋮ del pedido.',
      'Acá seleccioná el pedido en la lista de pendientes.',
      'Leé cada artículo con el lector de códigos (o escribí el código y Enter). Para varias unidades, cargá la cantidad antes de leer.',
      'Cuando no queden pendientes presioná F2 - Listo. El pedido pasa a Preparado y se puede facturar.',
    ],
    campos: [
      'Cant. Pendientes / Pickeadas / Pedidas: filtran los artículos del pedido según su avance.',
      'UM: unidad de medida del renglón del pedido (detalle de la transacción).',
      'Eventos: historial de lecturas y correcciones con usuario y hora.',
    ],
    errores: [
      'Si un código no pertenece al pedido o supera lo pedido, la lectura se rechaza, el lector se clava y hay que aceptar la alerta a mano.',
      'Una lectura equivocada se corrige con el botón deshacer de la línea.',
    ],
    tips: [
      'Si cerrás con artículos pendientes, el sistema pregunta si cancelás lo pendiente o lo mandás a un pedido en espera. El lector queda bloqueado hasta que elijas.',
    ],
    links: [{ label: 'Pedidos', to: ROUTES.ventas.pedidos.list }],
  }
);

const helpFacturasListadoVentas = hp(
  'Facturas de venta (listado)',
  'Consulta del listado de facturas emitidas con filtros y acceso al detalle o navegación al comprobante.',
  {
    pasos: [
      'Filtrá por fechas y cliente cuando exista.',
      'Abrí una fila para ver detalle o continuar edición si está permitido.',
      'Para emitir una nueva factura usá “Nuevo” o la acción equivalente.',
    ],
    links: [{ label: 'Nueva factura', to: ROUTES.ventas.facturas.pos }],
  }
);

const helpRemitosVenta = hp(
  'Remitos de venta',
  'Gestión de remitos de entrega vinculados al circuito de ventas y logística.',
  {
    queHace:
      'Desde el listado abrís el detalle de un remito (como en remitos de compra) para ver encabezado, renglones y lotes asignados.',
    pasos: [
      'Creá o buscá remitos según política de la empresa.',
      'Asociá cliente, transporte y líneas cuando la pantalla lo permita.',
      'Con Buscar pendientes podés incorporar renglones del pedido; si el artículo controla lote o serie, aparece el ícono de caja en Stock para asignarlos antes de guardar.',
      'En el listado, abrí el comprobante o el ícono Ver para ir al detalle completo.',
      'La anulación de un remito revierte su movimiento de stock. Solo pide clave si SucVenta.PassAnulacion tiene un IdPassword asignado para esa sucursal y tipo remito.',
    ],
    campos: [
      'Lotes (detalle): chips con el número de lote; al hacer clic abrís el popup de consulta.',
      'Certificados / documentos del lote: en el detalle solo se pueden descargar; para cargar o eliminar usá Inventario → Lotes.',
      'Cantidad / UM en el detalle: siempre la unidad base del artículo. Si cargaste otra UM con equivalencia, aparecen también Cant. Ped / UM Ped.',
    ],
    tips: [
      'Verificá depósito y ubicación si el flujo involucra stock.',
      'Al guardar, la cantidad y UM que ingresaste quedan en el renglón (campos Umped); Items.Cantidad/UM y el movimiento de stock van en UM base.',
      'Sin ubicación de egreso no se habilita la asignación de lotes en las líneas.',
      'Al asignar lotes en remito de venta solo aparecen lotes con saldo en esa ubicación. Si en Inventario → Lotes ves saldo pero acá “Sin lotes”, el stock está en otro depósito: cambiá la ubicación o transferí.',
      'Si un renglón del detalle no muestra lotes, puede ser que el artículo no controle lote o que el egreso no haya generado movimiento de lote.',
    ],
  }
);

const helpNotasCreditoVenta = hp(
  'Notas de crédito de venta',
  'Emisión y seguimiento de notas de crédito sobre facturas de venta.',
  {
    pasos: [
      'Indicá factura origen o cliente según corresponda.',
      'Revisá importes, alícuotas y letra antes de confirmar.',
    ],
    campos: [
      'Las líneas copiadas de la factura origen conservan la unidad de ese comprobante (snapshot Fac).',
      'Un artículo agregado sin documento anterior toma UM Ref. Facturación. Si está vacía, usa la unidad de medida base. El stock se muestra en esa unidad, con coma decimal.',
      'En el detalle, Cantidad y UM son la base; Cant. Fac / UM Fac aparecen solo si la UM ingresada difiere de la base.',
    ],
    errores: ['Montos que excedan lo facturable pueden ser rechazados por validaciones.'],
  }
);

const helpContratosVenta = hp(
  'Contratos de venta',
  'Gestión de contratos con facturación periódica o por avances certificados, integrados a ARCA/AFIP.',
  {
    queHace:
      'Administra contratos de venta y su facturación con un formulario tipo POS. El módulo debe estar habilitado en la biblioteca de módulos (CONTRATOS_VENTA).',
    pasos: [
      'En el panel izquierdo elegí el cliente y cargá los productos del contrato.',
      'En el panel derecho completá compañía, sucursal, ubicación de origen, moneda, fechas y periodicidad.',
      'Usá variables en observaciones o en la descripción ({cuota}, {mes}, {oc}, etc.).',
      'Activá el contrato para generar períodos y habilitar facturación.',
      'Cargá certificaciones de avance cuando el ítem sea por porcentaje.',
      'Desde Facturación masiva previsualizá y confirmá las facturas pendientes.',
    ],
    campos: [
      'Ubicación origen: se guarda en el contrato y se usa al facturar, igual que en la factura manual.',
      'Sucursal venta: determina la numeración/compañía del comprobante; no elige sola la ubicación.',
    ],
    tips: [
      'Las notas de crédito sobre facturas de contrato vuelven a dejar pendientes los períodos o avances.',
      'Si no ves Contratos en el menú, habilitalo en Configuraciones → Biblioteca de módulos.',
      'Los contratos ya existentes sin ubicación hay que editarlos y asignarla antes de facturarlos.',
    ],
    errores: [
      'Sin ubicación de origen el contrato no se puede guardar ni facturar.',
    ],
    links: [
      { label: 'Listado de contratos', to: ROUTES.ventas.contratos.list },
      { label: 'Facturación masiva', to: ROUTES.ventas.contratos.facturacion },
    ],
  }
);

const helpContratosFacturacion = hp(
  'Facturación masiva de contratos',
  'Previsualiza períodos y avances vencidos y emite facturas con CAE de ARCA.',
  {
    queHace:
      'Emite en lote las facturas pendientes de contratos. Requiere el módulo Contratos de venta habilitado.',
    pasos: [
      'Elegí la fecha de corte y actualizá la previsualización.',
      'Usá Tildar todo o Destildar todo para marcar o desmarcar todas las líneas, o ajustá la selección una por una.',
      'Revisá descripción, cotización y selección de líneas.',
      'Confirmá la emisión; cada contrato se procesa por separado.',
    ],
    tips: [
      'La ubicación de origen sale del contrato (no se infiere por sucursal). Si falta, editá el contrato antes de facturar.',
    ],
  }
);

const helpVentasInformesHub = hp(
  'Informes de ventas',
  'Centro de acceso a informes comerciales: resultados de ventas, seguimiento, cuentas por cobrar, IVA ventas y resúmenes de cuenta.',
  {
    pasos: [
      'Elegí el informe según lo que necesites analizar.',
      'Completá parámetros de fechas y filtros antes de ejecutar.',
    ],
    tips: ['Muchos informes permiten exportar o imprimir desde la barra de herramientas.'],
  }
);

const helpInformePersonalizadoContabilidad = hp(
  'Informe de contabilidad',
  'Armado de un informe sobre los renglones del libro diario y las cuentas del plan.',
  {
    queHace: 'Muestra los movimientos del período, incluso si la cuenta de la línea no está cargada en imputables, y permite filtrarlos, resumirlos y exportarlos a Excel.',
    pasos: [
      'Indicá el período. La fecha es la del asiento y, si el asiento no tiene fecha, la de la línea.',
      'Para una cuenta puntual, cargala en Cuenta o como filtro adicional Número de cuenta. Ese criterio se aplica en la consulta, antes del máximo de filas.',
      'Revisá la vista previa y descargá Excel si hace falta.',
    ],
    campos: [
      'Cuenta: número de la línea o, si la cuenta no tiene líneas, el del plan. Una línea sin imputable igual aparece y el rubro queda vacío.',
      'Una cuenta del plan sin movimientos queda en el origen, sin fecha ni importes. La vista previa por período no la lista porque no tiene fecha de asiento.',
      'Máximo de filas: tope de la vista previa, hasta 15.000. El filtro de cuenta se aplica antes de ese tope.',
    ],
    errores: [
      'Si el período no incluye la fecha del asiento, esas líneas no entran aunque existan en el detalle.',
      'Un filtro adicional distinto del número de cuenta se aplica sobre las filas ya cargadas. Si la vista corta en el máximo, acotá el período o subí el máximo.',
    ],
    tips: ['Guardar persiste la definición. La vista previa lee los movimientos actuales.'],
  }
);

const helpInformesPersonalizados = hp(
  'Mis informes',
  'Reportes de la biblioteca instalados por la empresa.',
  {
    pasos: [
      'Ejecutá un informe instalado e indicá los parámetros si los pide.',
      'Si sos administrador, abrí el store para instalar o quitar informes de la empresa.',
    ],
    tips: ['En ventas, compras y contabilidad, el informe personalizado está en la sección Mis informes.'],
  }
);

const helpInformeSeguimientoVentas = hp(
  'Seguimiento de ventas',
  'Seguimiento de pedidos/cotizaciones/facturas en curso para priorizar entregas y cobranzas.',
  {
    pasos: ['Filtrá por vendedor, cliente o fecha.', 'Usá las acciones por línea para avanzar el proceso.'],
    tips: ['Ideal para vista operativa diaria del equipo comercial.'],
  }
);

const helpInformeCuentasCobrar = hp(
  'Cuentas por cobrar',
  'Análisis de deudas de clientes y vencimientos para cobranzas.',
  {
    queHace:
      'Muestra saldos de clientes y permite importar saldos iniciales como créditos/débitos internos.',
    pasos: [
      'Definí fechas y filtros de cliente o vendedor.',
      'Importar saldos: sucursal → Excel → Validar y ver previa → confirmar.',
      'Exportá o generá listados según necesites.',
    ],
    tips: [
      'Importar saldos crea comprobantes 80/81 letra X con ítem y asiento.',
      'El listado y alta unitaria de NC/ND internas está en Ventas → Movimientos internos.',
    ],
    links: [
      { label: 'Movimientos internos', to: '/ventas/movimientos-internos' },
    ],
  }
);

const helpMovimientosInternosCxc = hp(
  'Movimientos internos',
  'Listado de créditos y débitos internos de clientes (formularios 80/81), con alta POS, impresión y anulación.',
  {
    queHace:
      'Consulta los comprobantes internos de cuenta corriente clientes (letra X), permite crear NC/ND internas e imprimir o anular.',
    pasos: [
      'Filtrá por fechas, tipo (80/81), estado y sucursal.',
      'Crédito interno / Débito interno abre el alta POS.',
      'En cada fila: imprimir PDF o anular (revierte el asiento).',
    ],
    campos: [
      'Tipo 80 = crédito interno; 81 = débito interno.',
      'Sucursal: letra X y numerador FormuN de SucVenta.',
    ],
    errores: [
      'Se rechazan clientes inexistentes, artículos sin CtaVenta, SucVenta/FormuN faltante o importe negativo.',
    ],
    tips: [
      'La importación masiva de saldos está en Cuentas por cobrar → Importar saldos.',
      'Volvé al módulo Ventas con el botón de atrás.',
      'La anulación no mueve stock; solo revierte el asiento.',
    ],
    links: [
      { label: 'Ventas', to: '/ventas' },
      { label: 'Cuentas por cobrar', to: '/ventas/informes/cuentas-cobrar' },
    ],
  }
);

const helpInformeCuentasPagar = hp(
  'Cuentas por pagar',
  'Análisis de deudas con proveedores y vencimientos para pagos.',
  {
    queHace:
      'Muestra saldos de proveedores y permite importar saldos iniciales como créditos/débitos internos.',
    pasos: [
      'Filtrá y ordená el listado de proveedores con saldo.',
      'Importar saldos: sucursal → Excel → Validar y ver previa → confirmar.',
      'Exportá o generá listados según necesites.',
    ],
    tips: [
      'Importar saldos crea comprobantes 80/81 letra X con ítem y asiento.',
      'El listado y alta unitaria de NC/ND internas está en Compras → Movimientos internos.',
    ],
    links: [
      { label: 'Movimientos internos', to: '/compras/movimientos-internos' },
    ],
  }
);

const helpMovimientosInternosCxp = hp(
  'Movimientos internos',
  'Listado de créditos y débitos internos de proveedores (formularios 80/81), con alta POS, impresión y anulación.',
  {
    queHace:
      'Consulta los comprobantes internos de cuenta corriente proveedores (letra X), permite crear NC/ND internas e imprimir o anular.',
    pasos: [
      'Filtrá por fechas, tipo, estado y sucursal.',
      'Crédito interno / Débito interno abre el alta POS.',
      'En cada fila: imprimir PDF o anular (revierte el asiento).',
    ],
    campos: [
      'Tipo 80 = crédito interno; 81 = débito interno.',
      'Sucursal: letra X y numerador FormuN de SucVenta.',
    ],
    errores: [
      'Se rechazan proveedores inexistentes, artículos sin CtaCompra, SucVenta/FormuN faltante o importe negativo.',
    ],
    tips: [
      'La importación masiva de saldos está en Cuentas por pagar → Importar saldos.',
      'Volvé al módulo Compras con el botón de atrás.',
      'La anulación no mueve stock; solo revierte el asiento.',
    ],
    links: [
      { label: 'Compras', to: '/compras' },
      { label: 'Cuentas por pagar', to: '/compras/informes/cuentas-pagar' },
    ],
  }
);

const helpInformeResumenCuentaVentas = hp(
  'Resumen de cuenta (cliente)',
  'Estado de cuenta y movimientos de un cliente seleccionado.',
  {
    pasos: ['Seleccioná cliente y rango de fechas.', 'Revisá cobranzas aplicadas y saldos.'],
    links: [{ label: 'Clientes', to: '/ventas/clientes' }],
  }
);

const helpInformeLibroIvaVentas = hp(
  'Libro IVA ventas',
  'Informe fiscal de ventas para presentaciones y auditoría.',
  {
    pasos: ['Validá período fiscal y punto de venta si aplica.', 'Exportá según formato requerido por tu estudio contable.'],
    errores: ['Comprobantes mal clasificados pueden figurar en incorrecto régimen de IVA.'],
  }
);

const helpInformeVentasDinamico = hp(
  'Informe de ventas dinámico',
  'Consulta dinámica de ventas con agrupaciones y filtros extendidos.',
  {
    pasos: ['Seleccioná dimensiones de análisis (cliente, período, artículo).', 'Guardá vistas útiles como referencia interna.'],
  }
);

const helpInformeVentasPorProducto = hp(
  'Informe de ventas por producto',
  'Resumen agrupado por producto, tipo de artículo, ramo, línea y clase (NombreRamo, NombreLinea, NombreClase en BP_RPT_Ventas01), con totales y detalle de líneas. En filtros avanzados podés discriminar comprobantes categorizados y no categorizados.',
  {
    pasos: [
      'Definí fechas y filtros opcionales por sucursal, tipo, ramo, línea y clase.',
      'En filtros avanzados elegí Todos, Categorizado o No Categorizado para ver el volumen real por línea o ramo.',
      'Generá el informe y exportá a CSV o Excel o imprimí la tabla.',
      'Usá Ver Detalle para ver las líneas que componen cada grupo.',
    ],
  }
);

const helpInformeVentasResultados = hp(
  'Informe de resultados de ventas',
  'Listado de ventas del período por comprobante (mostrador y resto), con vendedor, forma de pago y detalle de artículos.',
  {
    pasos: [
      'Definí fechas y, si querés, filtrá por cliente, sucursal u ubicación.',
      'Generá el informe y expandí cada comprobante para ver los artículos.',
      'Exportá a CSV, Excel o PDF según necesites.',
    ],
    tips: ['Ideal para seguir las ventas del día o del período en el mostrador.'],
  }
);

const helpVentasMaestrosAux = hp(
  'Maestros de ventas',
  'Administración de listas de precios, vendedores, rutas, visitas y tablas auxiliares del circuito comercial.',
  {
    pasos: ['Editá registros desde la grilla o formulario.', 'Verificá impacto en precios y comisiones al cambiar datos.'],
    tips: ['Los cambios en listas de precios pueden afectar cotizaciones futuras.'],
  }
);

const helpArticuloListaPrecio = hp(
  'Artículos por lista de precio',
  'Relación entre artículos y listas de precios: vigencia, precios y condiciones.',
  {
    queHace:
      'Define el precio de venta de cada artículo en una lista, indicando en qué unidad de medida está expresado ese precio.',
    pasos: [
      'Seleccioná lista y artículo.',
      'Ingresá el precio de venta y la UM del precio (si la dejás vacía, se usa la UM base del artículo).',
      'Guardá cambios por ítem, importación Excel o alta masiva.',
    ],
    campos: [
      'Precio Venta: monto de 1 unidad de la UM indicada.',
      'UM: unidad a la que corresponde el precio; al vender se convierte a la UM del documento con las equivalencias del artículo.',
    ],
    errores: [
      'Precio cero o sin moneda pueden invalidar cotizaciones.',
      'Si la UM del precio no existe en el maestro, el alta/edición se rechaza.',
    ],
    tips: [
      'Si el documento usa otra UM (p. ej. precio en BOL y factura en KG), el sistema convierte con las equivalencias del artículo; sin equivalencia no inventa factor.',
    ],
  }
);

const helpComprasFacturas = hp(
  'Facturas de compra',
  'Registro y consulta de facturas de proveedor: ítems, impuestos, imputaciones y vínculo con órdenes y remitos.',
  {
    pasos: [
      'Desde listado: filtrá y abrí un comprobante para verificar o auditar.',
      'Desde nuevo: cargá proveedor, compañía fiscal, ítems y totales según factura física.',
    ],
    campos: ['Tipo de ítem producto vs gasto/concepto según configuración.', 'Percepciones y retenciones si aplican.'],
    tips: ['Conciliar contra orden de compra o remito cuando exista flujo formal.'],
    links: [{ label: 'Órdenes de compra', to: '/compras/ordenes' }],
  }
);

const helpComprasOrdenes = hp(
  'Órdenes de compra',
  'Creación y seguimiento de órdenes a proveedores antes de facturar o recibir mercadería.',
  {
    pasos: ['Definí proveedor, condiciones y líneas solicitadas.', 'Actualizá estado cuando recibas mercadería o factura.'],
    campos: [
      'UM: se completa al cargar el producto (UM de compra del artículo/proveedor) y no es editable en el renglón.',
    ],
    errores: ['Cantidades toleradas pueden estar limitadas por política de compras.'],
  }
);

const helpComprasRemitos = hp(
  'Remitos de compra',
  'Recepciones de mercadería contra órdenes o facturas.',
  {
    pasos: [
      'Registrá cantidades recibidas por línea.',
      'Verificá depósito destino para stock.',
      'Si el artículo controla lote, asigná lotes al cargar el remito; en el detalle se muestran por renglón.',
    ],
    campos: [
      'UM: al traer un pendiente hereda la del renglón de la orden o factura de compra; si se agrega un artículo a mano, usa la UM de compra del producto. No es editable en el remito.',
      'Lotes (detalle): chips con el número/cantidad; al abrir el popup ves la asignación y los certificados del lote.',
    ],
    tips: [
      'Útil como paso previo a la factura de compra en muchos procesos.',
      'Si un renglón no muestra lotes, puede ser que el artículo no controle lote o que el ingreso no haya generado movimiento de lote.',
      'Desde el detalle del remito solo podés descargar certificados; para subir o borrar archivos usá Inventario → Lotes.',
      'Al usar Agregar todos con varios artículos que controlan lote, el sistema abre la asignación en cola: al cerrar un diálogo pasa al siguiente.',
    ],
  }
);

const helpProveedores = hp(
  'Proveedores',
  'Maestro de proveedores: datos impositivos, condiciones de compra y categorización.',
  {
    pasos: [
      'Buscá antes de dar de alta para evitar duplicados.',
      'Completá CUIT y categoría fiscal con precisión.',
      'En Extras, cargá la Nota 1 (uso interno) con observaciones operativas (cheques, flete/sobres, plazos comerciales, etc.); se usa luego en la impresión de OP tipo P.',
    ],
    links: [{ label: 'Facturas de compra', to: '/compras/facturas' }],
  }
);

const helpRamosProv = hp(
  'Ramos de proveedores',
  'Clasificación de proveedores por ramo para informes y políticas comerciales.',
  {
    pasos: ['Mantené códigos claros y sin duplicar descripciones.'],
  }
);

const helpNotasCreditoCompraLista = hp(
  'Notas de crédito de compra',
  'Listado y consulta de notas de crédito recibidas; acceso al detalle por número de formulario.',
  {
    pasos: ['Filtrá por fechas o proveedor.', 'Para nueva nota usá la opción correspondiente desde el listado.'],
    links: [
      { label: 'Nueva nota de crédito', to: '/compras/notas-credito/nuevo' },
      { label: 'Facturas de compra', to: '/compras/facturas' },
    ],
  }
);

const helpComprasInformesHub = hp(
  'Informes de compras',
  'Informes de cuentas a pagar, libro IVA compras, resumen de cuenta proveedor y seguimiento.',
  {
    pasos: ['Elegí informe y definí período.', 'Validá totales contra facturas registradas.'],
  }
);

const helpInformeComprasDinamico = hp(
  'Informe de compras dinámico',
  'Consulta dinámica de compras con agrupaciones y filtros extendidos.',
  {
    pasos: ['Seleccioná dimensiones de análisis (proveedor, período, artículo).', 'Guardá vistas útiles como referencia interna.'],
  }
);

const helpSeguimientoCompras = hp(
  'Seguimiento de compras',
  'Seguimiento operativo del circuito de compras pendientes.',
  {
    pasos: ['Priorizá por fecha prometida o proveedor.', 'Coordiná con depósito y recepción.'],
  }
);

const helpInventarioProductos = hp(
  'Productos',
  'Maestro de artículos: códigos, descripciones, unidades, precios base y datos de stock.',
  {
    queHace:
      'Permite alta, edición y mantenimiento masivo de artículos. Exportar Excel usa el mismo formato que la plantilla de importación para editar y reimportar.',
    pasos: [
      'Buscá por código o descripción antes de crear uno nuevo.',
      'Completá unidad de medida y tipo de ítem (incluye Semi Elaborado).',
      'En Equivalencias, definí conversiones desde otras UM hacia la UM base (1 origen = cantidad destino).',
      'Si el artículo controla lote o serie, elegí la máscara de numeración preferida (opcional).',
      'Para cambios masivos: Exportar Excel (respeta filtros), editá el archivo y usá Importar productos. Si el código existe se actualiza; si no, se crea.',
    ],
    campos: [
      'Tipo de artículo: entre otros, PT (Producto Terminado) y SE (Semi Elaborado). El SE se puede fabricar con receta y también usarse como componente.',
      'Unidad de medida (tab Stock): es la UM base del artículo. Si Cantidad Actual es distinta de 0, el campo queda bloqueado.',
      'Stock UM ref (listado): columna opcional con la cantidad convertida a la UM de referencia de stock cuando está configurada y hay equivalencia.',
      'Equivalencias: cada fila se lee 1 [UM origen] = [cantidad] [UM base]. La UM destino es siempre la base y no se puede modificar.',
      'Control de lote/serie: Sin control, Por lote o Por serie. Con stock distinto de cero no se puede cambiar el seguimiento.',
      'Máscara de numeración: plantilla sugerida al generar números de lote/serie; se administra en Máscaras de numeración.',
      'Excel import/export: incluye moneda (tipoMoneda), UM base, UM ref stock, tipo, lote/vencimiento, inventariado, comisión, no bonificable y cuentas contables. Ver hoja Instrucciones del archivo.',
    ],
    errores: [
      'Código duplicado: el sistema suele impedir guardar.',
      'Con stock distinto de cero no se puede cambiar la unidad de medida base, la UM de referencia de stock, el tipo de artículo ni el control de lote/serie; primero llevá la cantidad a 0 (ajustes/conteo).',
      'Sin UM base definida no se pueden agregar equivalencias.',
      'En importación, si una fila intenta cambiar UM, tipo o lote con stock ≠ 0, esa fila falla y el resto continúa.',
    ],
    tips: [
      'Por defecto el listado prioriza código, descripción, precio (con moneda), stock y acciones. Ramo, línea, clase, estado y Stock UM ref se activan desde personalizar columnas.',
      'En Acciones: Info 360 y Editar quedan visibles; etiquetas, copiar y eliminar están en el menú ⋮.',
      'Los filtros activos se muestran como chips debajo de la búsqueda; podés quitarlos uno a uno o limpiarlos todos.',
      'Ejemplo: 1 MAPLE = 30 UNIDADES, con UNIDADES como UM base.',
      'Exportar Excel y la plantilla de Importar usan las mismas columnas: podés exportar, editar y reimportar sin reformatear.',
      'En Info 360, Reservado solo baja el disponible si el tipo Pedido tiene Descuenta stock activo. Si no, la lista de pedidos se muestra con un aviso.',
      'Si el artículo controla lote o serie, Info 360 muestra la pestaña Lotes (datos de info-360: lotesDisponibles) con saldo y documentos por lote.',
    ],

    links: [
      { label: 'Lotes', to: '/inventario/lotes' },
      { label: 'Transferencias', to: '/inventario/transferencias' },
      { label: 'Máscaras de numeración', to: '/inventario/lotes/numeracion' },
    ],
  }
);

const helpLotesNumeracion = hp(
  'Máscaras de numeración',
  'Alta y mantenimiento de plantillas para generar números de lote o serie automáticamente.',
  {
    queHace:
      'Permite definir máscaras con tokens de fecha, código de artículo y secuencia, asociarlas a un artículo (ámbito por artículo) o asignarlas a varios productos.',
    pasos: [
      'Creá una máscara con nombre, plantilla y ámbito (global o por artículo).',
      'Si es por artículo, buscá el producto con el buscador; al guardar se asocia solo a ese artículo.',
      'Para varios productos usá solo máscaras globales: Buscar producto y Asignar.',
      'También podés elegir la máscara en el maestro de productos (control lote/serie).',
    ],


    campos: [
      'Máscara: tokens {YYYY}, {YY}, {MM}, {DD}, {COD} (código artículo) y {SEQ:n} (secuencia de n dígitos).',
      'Ámbito Global: disponible para cualquier artículo. Por artículo: requiere código y aplica solo a ese ítem.',
      'Último N°: contador ya reservado por generaciones reales (no por la vista previa).',
      'Activa: las inactivas no aparecen en el asistente ni en el selector del producto.',
    ],
    errores: [
      'Sin nombre o máscara el alta no se guarda.',
      'Ámbito por artículo exige código de artículo.',
      'Si el servidor rechaza la máscara, revisá tokens y longitud (máx. 80).',
    ],
    tips: [
      'Ejemplo: LOT-{YYYY}{MM}-{SEQ:4} → LOT-202610-0001.',
      'La eliminación falla si la máscara está en uso; en ese caso desactivala.',
      'Ámbito por artículo: ideal cuando un solo producto usa esa plantilla.',
    ],

    links: [
      { label: 'Lotes', to: '/inventario/lotes' },
      { label: 'Productos', to: '/inventario/productos' },
    ],
  }
);

const helpLotesDetalle = hp(
  'Detalle de lote',
  'Consulta y edición de un lote o serie: estado, vencimiento, saldos, movimientos, trazabilidad y documentos adjuntos.',
  {
    queHace:
      'Muestra los datos del lote, permite actualizar estado u observaciones, y gestionar certificados u otros archivos asociados.',
    pasos: [
      'Revisá saldos por depósito y movimientos.',
      'Ajustá estado (activo/bloqueado), vencimiento u observaciones y guardá.',
      'En Documentos del lote: subí, previsualizá, descargá o eliminá adjuntos.',
      'En Movimientos, hacé clic en el documento para abrir el comprobante.',
    ],
    campos: [
      'Estado Activo / Bloqueado: un lote bloqueado no debería usarse en egresos.',
      'Adjuntos: PDF, imágenes y documentos de oficina; elegí el tipo (certificado, análisis, otro) al subir.',
      'Documento (movimientos): enlace al comprobante que originó el movimiento.',
    ],
    tips: [
      'La vista previa abre el archivo en el navegador; Descargar lo guarda en el equipo.',
      'Un lote pertenece a un solo artículo; no se comparte entre productos.',
    ],
    links: [
      { label: 'Lotes', to: '/inventario/lotes' },
      { label: 'Máscaras de numeración', to: '/inventario/lotes/numeracion' },
    ],
  }
);

const helpLotesListado = hp(
  'Lotes',
  'Consulta y alta de lotes/series por artículo. Desde acá también accedés a las máscaras de numeración.',
  {
    pasos: [
      'Filtrá por código de artículo y/o número de lote/serie.',
      'Abrí una fila para ver saldos, movimientos, trazabilidad y adjuntos.',
      'Usá Nuevo lote para registrar un lote manual, o Máscaras para administrar plantillas.',
    ],
    tips: [
      'Cada lote es de un solo producto. La máscara preferida se define en el producto o en la pantalla de máscaras.',
      'El saldo aparece cuando ingresás stock (remito, ajuste, etc.) asociado a ese lote.',
      'El saldo de la grilla es el total. Abrí el detalle del lote para ver en qué depósito está; un remito de venta solo ofrece lotes con saldo en la ubicación de egreso.',
    ],
    links: [
      { label: 'Máscaras de numeración', to: '/inventario/lotes/numeracion' },
      { label: 'Productos', to: '/inventario/productos' },
    ],
  }
);

const helpRecetasProduccion = hp(
  'Recetas de producción',
  'Definición de la fórmula (batch) de un Producto Terminado o Semi Elaborado y sus componentes.',
  {
    queHace:
      'Permite cargar o editar la receta: producto a fabricar, cantidad de batch y componentes con sus cantidades.',
    pasos: [
      'Indicá código y descripción de la receta.',
      'Elegí el producto a fabricar (PT o SE) y la cantidad de batch.',
      'Cargá los componentes (MP, PT, SE o SER) con sus cantidades.',
      'Reordená las líneas con las flechas de Orden si hace falta.',
      'Completá Notas / procedimientos si querés documentar el proceso.',
      'Guardá. Si aplica, confirmá actualizar el costo del producto en la ficha.',
      'Para duplicar: en el listado usá Copiar; se abre el alta con los datos precargados (código vacío para asignar uno nuevo).',
    ],
    campos: [
      'Cantidad de producto / componentes: puede ser cero. No se permiten valores negativos.',
      'Decimales: se permiten según la cantidad de decimales configurada en la UM del artículo; si la UM no admite decimales, solo enteros.',
      'Orden: las flechas suben o bajan cada componente; al guardar se conserva ese orden.',
      'Notas / procedimientos (cabecera): texto libre general de la receta.',
      'Notas / proc. (por renglón): procedimiento o detalle específico de cada componente.',
      'Semi Elaborado (SE): puede ser el producto de la receta o un componente de otra.',
    ],
    errores: [
      'Debe haber al menos un componente.',
      'El producto a fabricar no puede ser componente de su propia receta.',
      'Si el batch queda en cero, la receta se guarda pero no se podrá confirmar una orden de producción hasta definir cantidad de batch mayor a cero.',
      'Al copiar, el código debe ser nuevo: si ya existe, el alta falla.',
    ],
    tips: [
      'El costo de la receta se calcula con costo × cantidad de cada componente.',
      'Copiar no guarda sola: revisá el formulario y guardá con el código nuevo.',
      'En Acciones: Editar queda visible; Imprimir PDF, Copiar y Eliminar están en el menú «…».',
    ],
    links: [
      { label: 'Órdenes de producción', to: '/produccion/ordenes' },
      { label: 'Productos', to: '/inventario/productos' },
    ],
  }
);

const helpOrdenesProduccion = hp(
  'Órdenes de producción',
  'Alta y seguimiento de órdenes para fabricar un Producto Terminado o Semi Elaborado según su receta.',
  {
    queHace: 'Genera una orden asociada a un artículo fabricable (PT o SE) y su receta.',
    pasos: [
      'Elegí el artículo a fabricar (PT o SE).',
      'Seleccioná o confirmá la receta asociada.',
      'Indicá cantidad, depósitos y guardá.',
    ],
    campos: [
      'Código artículo: Producto Terminado o Semi Elaborado con receta.',
    ],
    errores: [
      'Sin receta o con batch en cero no se puede confirmar la producción.',
    ],
    links: [{ label: 'Recetas', to: '/produccion/recetas' }],
  }
);

const helpInventarioTransferencias = hp(
  'Ajustes y transferencias',
  'Movimientos de stock entre ubicaciones o ajustes por diferencias.',
  {
    queHace:
      'Permite dar de alta ajustes positivos, negativos o transferencias entre ubicaciones. Al abrir un comprobante ya guardado solo se consulta: no se puede editar.',
    pasos: [
      'Desde el listado creá un ajuste positivo, negativo o una transferencia.',
      'Indicá depósito origen y/o destino según el tipo.',
      'Detallá líneas con cantidades y motivo si aplica, y guardá.',
      'Para revisar un comprobante existente, abrilo desde el listado: la pantalla es solo lectura.',
    ],
    tips: [
      'Verificá lotes o series si el artículo lo requiere.',
      'Si necesitás corregir stock de un movimiento ya guardado, generá un nuevo ajuste o transferencia compensatorio.',
    ],
  }
);

const helpInventarioClasificacion = hp(
  'Clasificación de productos',
  'Ramos, líneas y clases para organizar el catálogo y reportes.',
  {
    pasos: ['Asigná códigos únicos y jerarquías coherentes.', 'Evitá borrar si hay productos asociados.'],
  }
);

const helpUnidadesMedida = hp(
  'Unidades de medida',
  'Definición de unidades usadas en compras, ventas, stock y producción.',
  {
    queHace:
      'Administrá el catálogo de unidades (código, nombre y cantidad de decimales) que luego se asocian a artículos y documentos.',
    pasos: [
      'Creá unidades claras (UN, KG, M2, etc.).',
      'Indicá cuántos decimales admite la unidad (0 a 6).',
      'Relacioná la unidad con productos desde el maestro de artículos.',
    ],
    campos: [
      'Código: identificador de la unidad; no se puede cambiar después del alta.',
      'Nombre: descripción corta (máx. 20 caracteres).',
      'Cantidad de decimales: precisión permitida al cargar cantidades con esa UM. Con 0 solo se admiten enteros.',
    ],
    tips: [
      'En recetas y cargas con cantidades, el sistema respeta los decimales configurados en la UM del artículo.',
    ],
  }
);

const helpDescuentos = hp(
  'Ofertas',
  'Ofertas comerciales con vigencia. Cada oferta puede aplicar a un cliente, una ruta, una lista, un artículo u otra dimensión; no hace falta completar todas.',
  {
    pasos: [
      'Usá los filtros avanzados del listado para acotar por lista, cliente, artículo, atributos u otras dimensiones.',
      'Completá solo las dimensiones a las que aplica la regla.',
      'Indicá el porcentaje y, si corresponde, la fecha de caducidad (válido todo ese día; al siguiente ya no aplica).',
      'Al cargar el listado, las ofertas activas cuya vigencia ya venció se inactivan automáticamente.',
      'El backend decide a quién aplica según unidad de medida, cantidades y precios.',
    ],
    errores: ['Si el servidor rechaza la combinación, el mensaje indica el motivo; no se valida en pantalla.'],
  }
);

const helpInventarioOperativo = hp(
  'Operaciones de inventario',
  'Devoluciones, producción, picking o conteo físico según la pantalla.',
  {
    pasos: ['Seguí el flujo indicado en cada paso de la pantalla.', 'Confirmá ubicaciones y cantidades relevadas.'],
    errores: ['Diferencias grandes pueden requerir ajuste supervisor.'],
  }
);

const helpOrdenesDevolucion = hp(
  'Órdenes de devolución',
  'Alta de devoluciones a partir de remitos de venta con cantidades pendientes de devolver.',
  {
    queHace: 'Selecciona un remito con pendiente y arma la orden con las líneas a devolver.',
    pasos: [
      'Buscá remitos con pendiente de devolución.',
      'Marcá las líneas y ajustá la cantidad a devolver.',
      'Confirmá para generar la orden.',
    ],
    campos: [
      'UM: unidad de medida del renglón del remito origen (detalle de la transacción).',
    ],
    tips: ['La UM se toma del pendiente del remito y se reenvía al crear la orden.'],
  }
);

const helpConteoStock = hp(
  'Conteo de stock',
  'Relevamiento físico de existencias por ubicación; el sistema contrasta lo contado con el stock teórico y genera los ajustes correspondientes.',
  {
    queHace:
      'Permite cargar o consultar un conteo por sucursal y ubicación, con el detalle de productos, cantidades teóricas, contadas y la diferencia, indicando la unidad de medida de cada artículo.',
    pasos: [
      'Elegí sucursal, fecha y ubicación a relevar.',
      'Agregá productos por código, búsqueda o importación desde Excel.',
      'Completá la cantidad contada; la UM del artículo se muestra al lado de las cantidades.',
      'Revisá la diferencia (contado − teórico) y guardá el conteo.',
    ],
    campos: [
      'Teórico: stock del sistema en la ubicación al momento del conteo, con su UM.',
      'Contado: cantidad relevada físicamente; la UM del producto aparece junto al valor.',
      'Diferencia: contado menos teórico, en la misma UM del artículo.',
    ],
    errores: [
      'Sin ubicación válida no se puede obtener el stock teórico.',
      'Un código inexistente no carga descripción ni UM.',
    ],
    tips: ['Podés importar códigos y cantidades desde un archivo para agilizar conteos grandes.'],
  }
);

const helpProgramacionProduccion = hp(
  'Programación de producción',
  'Sugiere qué fabricar y cuándo, a partir del stock, las reservas, lo que ya está en producción o en borrador y las ventas de la ventana elegida. Solo entran artículos con receta.',
  {
    queHace:
      'Calcula una cantidad y una fecha por artículo y las guarda hasta el próximo cálculo. Ramo, línea, clase, búsqueda y los chips de prioridad solo filtran la grilla. Depósito, ventana, horizonte y ajuste de demanda se aplican cuando pulsá Calcular. Esos filtros se recuerdan en este navegador para tu usuario.',
    pasos: [
      'Elegí ramo, línea y clase si querés acotar. El depósito es opcional: sin depósito el stock es de toda la empresa.',
      'Elegí la ventana de ventas (30, 60, 90 o 180 días corridos hasta hoy, incluido), los días de horizonte a cubrir y el porcentaje de ajuste de demanda.',
      'Pulsá Calcular. Puede tardar varios minutos. Mientras procesa, la pantalla lo indica.',
      'Revisá prioridad, insumos y cantidad. El ícono de información de cada columna explica ese dato. El ícono de ajuste abre los parámetros de fabricación del artículo.',
      'Marcá las filas, o usá Seleccionar sugeridos para tomar las que tienen cantidad mayor a cero y receta válida. Ajustá la cantidad si hace falta y pulsá Generar órdenes.',
      'Excel e Imprimir PDF exportan el resultado con los filtros de la grilla. La cantidad que editaste se usa en el archivo y en la orden; no cambia el cálculo guardado.',
    ],
    campos: [
      'Ventana: ventas netas del período (las facturas suman y las notas de crédito restan). No usa los 6 meses cerrados de Sugerencia de compra.',
      'Horizonte: días de demanda que hay que cubrir. En un artículo podés reemplazarlo con Cobertura objetivo.',
      'Ajuste demanda: porcentaje sobre la demanda diaria, entre −100 y 500. Se aplica al volver a calcular.',
      'Factor estacional: si hay ventas del mismo mes del año pasado, sube o baja la demanda entre 0,5 y 2. Sin esas ventas, no cambia el cálculo.',
      'Depósito: el stock y lo reservado salen de esa ubicación. Lo que está en producción es de la empresa y no se parte por depósito.',
      'Oferta: stock menos reservado, más lo que está en producción, más lo programado en órdenes en borrador.',
      'Demanda diaria: ventas de la ventana divididas por todos los días de esa ventana (también los días sin ventas), luego el ajuste y el factor estacional. Nunca queda en negativo.',
      'Cobertura: días que dura la oferta a ese ritmo. Si no hay demanda y hay oferta, se muestra un guion. Si no hay demanda ni oferta, es cero.',
      'Cantidad sugerida: demanda del horizonte más el stock de seguridad (o el mínimo del artículo, si no cargaste seguridad), menos la oferta. Se redondea hacia arriba al lote y no supera el stock máximo.',
      'Fecha: si la cobertura no llega al horizonte, es hoy. Si sobra cobertura, se corre esos días. El lead time adelanta la fecha y no la deja antes de hoy.',
      'Prioridad. Crítico: no hay oferta y hay demanda, o la cobertura no alcanza el horizonte. Producir: hay cantidad sugerida. Normal: la oferta alcanza. Sobrestock: la oferta supera el máximo y no se sugiere fabricar. Sin movimiento: no hubo ventas en la ventana y no hay faltante. Pasá el mouse sobre la etiqueta para ver el detalle de esa fila.',
      'Insumos: Cubierto, Faltante parcial, Sin stock o Receta inválida. El stock de cada componente se reparte primero a los artículos más críticos. Semielaborado significa que el componente tiene receta y puede aparecer como otra línea. Tocá la etiqueta para ver requerido, stock, asignado y faltante.',
      'Parámetros del artículo: stock de seguridad (reemplaza el mínimo), lote mínimo, múltiplo de lote, lead time y cobertura objetivo. Un campo vacío deja el valor de siempre. Desmarcar «Usar estos parámetros» los deja fuera del cálculo. Hay que volver a calcular para aplicarlos.',
      'Cantidad de la grilla: vale para el Excel, el PDF y la orden que vas a generar. No modifica la sugerencia guardada.',
      'Indicadores de arriba: críticos, artículos a producir, unidades sugeridas y cobertura mediana del resultado filtrado.',
    ],
    errores: [
      'Si todavía no hay un cálculo, la grilla pide Calcular. Si el último falló, el aviso muestra el motivo del servidor.',
      '«Receta sin batch válido» indica que esa fila no se puede convertir en orden de producción.',
      'Seleccionar sugeridos o exportar sin un cálculo listo muestra un aviso y no genera el archivo.',
      'Generar órdenes exige al menos un artículo marcado con cantidad mayor a cero.',
    ],
    tips: [
      'Cambiar la búsqueda o un chip de prioridad no recalcula: solo cambia lo que ves. Depósito, ventana, horizonte y ajuste sí, cuando pulsá Calcular.',
      'El stock en rojo es negativo y aumenta lo que hace falta fabricar.',
      'La programación no crea las órdenes sola. El alta queda en la pantalla siguiente, en borrador, y no mueve stock.',
    ],
    links: [
      { label: 'Órdenes de producción', to: '/produccion/ordenes' },
      { label: 'Recetas', to: '/produccion/recetas' },
      { label: 'Sugerencia de compra', to: '/compras/informes/sugerencia-compra' },
    ],
  }
);

const helpProgramacionProduccionPreview = hp(
  'Órdenes en borrador',
  'Revisa si las cantidades elegidas en programación se pueden fabricar y crea una orden de producción en borrador por artículo.',
  {
    queHace:
      'Parte de los artículos que marcaste en programación. Ver factibilidad calcula insumos y faltantes según el depósito origen. Crear borradores da de alta las órdenes una por una, sin mover stock. Un faltante no impide el alta. Una receta sin batch válido no se convierte en orden.',
    pasos: [
      'Elegí depósito origen y depósito destino, y la fecha de inicio. La fecha se propone con la primera fecha sugerida.',
      'Pulsá Ver factibilidad para ver, por artículo, si se puede generar y el estado de insumos.',
      'Revisá el listado de faltantes. Podés bajar el Excel o abrir Sugerencia de compra: este listado no escribe esa sugerencia.',
      'Pulsá Crear borradores. Si una orden falla, las anteriores quedan creadas. Un segundo clic sobre las mismas no las vuelve a crear.',
      'Ir a órdenes abre el listado para seguir el alta. Volver regresa a la programación.',
    ],
    campos: [
      'Depósito origen: de ahí se toma el stock de insumos para la factibilidad.',
      'Depósito destino: depósito de la orden de producción.',
      'Fecha de inicio: se guarda en cada orden. Si la cambiás, hay que volver a ver la factibilidad.',
      'Puede generar: Sí cuando la receta tiene batch válido y la cantidad es mayor a cero. El motivo explica el caso contrario.',
    ],
    errores: [
      'Sin artículos seleccionados, volvé a programación y marcá las filas.',
      'Origen y destino tienen que ser depósitos válidos antes de calcular la factibilidad.',
      'Si una orden falla a mitad del alta, las ya creadas no se revierten. El aviso lista cuáles fallaron.',
    ],
    tips: [
      'Los depósitos que elegiste se recuerdan en este navegador para tu usuario.',
      'Las órdenes quedan en Borrador, con la observación «Generada desde programación de producción».',
    ],
    links: [
      { label: 'Volver a programación', to: '/produccion/programacion' },
      { label: 'Órdenes de producción', to: '/produccion/ordenes' },
      { label: 'Sugerencia de compra', to: '/compras/informes/sugerencia-compra' },
    ],
  }
);

const helpInventarioInformes = hp(
  'Informes de inventario',
  'Stock por depósito, movimientos y stock valorizado a fecha.',
  {
    pasos: [
      'Definí depósito y fechas según el informe.',
      'En stock valorizado, elegí la fecha de corte y, si querés, un depósito. Sin depósito se totaliza en todos. El valorizado usa el costo neto actual.',
    ],
    tips: [
      'Asignado en stock por depósito es lo comprometido en pedidos solo si el tipo Pedido tiene Descuenta stock activo; se expresa en la UM base del artículo.',
    ],
  }
);

const helpStockPorDeposito = hp(
  'Stock por depósito',
  'Consulta del stock físico, asignado y disponible por depósito.',
  {
    queHace:
      'Lista el stock por artículo y ubicación, con totales de cantidad, asignado y disponible. Podés filtrar, exportar a Excel o generar PDF.',
    pasos: [
      'Opcionalmente filtrá por código, descripción o ubicación y pulsá Buscar.',
      'Usá los chips de Línea (a la derecha de la cantidad de registros) para acotar por línea de producto; desplazá horizontalmente si hay muchas.',
      'Hacé clic en una fila para ir a los movimientos de ese artículo en el depósito.',
    ],
    campos: [
      'Cantidad: stock físico real en el depósito (UM base).',
      'Asignado: cantidades comprometidas en pedidos de venta.',
      'Disponible: físico − asignado + órdenes de compra pendientes.',
      'Chips de Línea: filtran al instante; “Todas” quita el filtro de línea.',
    ],
    tips: [
      'Asignado se calcula con reservas de pedidos solo si el tipo Pedido tiene Descuenta stock activo.',
      'PDF y Excel respetan los mismos filtros aplicados (incluida la línea).',
    ],
  }
);

const helpMovimientosStock = hp(
  'Movimientos de stock',
  'Consulta de movimientos de inventario por período, artículo, tipo y depósito.',
  {
    queHace:
      'Lista los movimientos del período con cantidad y unidad de medida del artículo para interpretar cada fila.',
    pasos: [
      'Indicá el rango de fechas y, si querés, artículo, tipo de movimiento o depósito.',
      'Revisá cantidad y U.M. de cada línea; el total de cantidad no asume una sola UM.',
    ],
    campos: [
      'U.M.: unidad de medida base del producto.',
      'Cantidad: se muestra junto a la UM del artículo (ej. 14 UN).',
    ],
  }
);

const helpTesoreriaRecibos = hp(
  'Recibos de cobranza',
  'Registro de cobranzas a clientes: medios de pago, imputación a facturas y valores recibidos.',
  {
    pasos: [
      'Seleccioná cliente y comprobantes a cancelar o imputar.',
      'Distribuí importes entre medios de cobro.',
      'Si falta o sobra dentro del máximo del MCP de redondeo, usá el botón Redondeo.',
      'Grabá y verificá numeración.',
    ],
    campos: [
      'Medios MCP, cheques y efectivo según parametrización.',
      'Caja o usuario responsable si aplica.',
      'Redondeo: solo medios MCP con el flag activo. Si falta o sobra dentro del máximo, se cuadra con ese medio.',
    ],
    tips: [
      'Conciliá con extracto bancario cuando el cobro sea transferencia.',
      'Si no hay MCP con redondeo en la caja, el sistema pedirá crear o configurar uno.',
    ],
    links: [
      { label: 'Facturas venta', to: '/ventas/facturas' },
      { label: 'Medios MCP', to: '/tesoreria/medios-mcp' },
    ],
  }
);

const helpTesoreriaDevoluciones = hp(
  'Devoluciones de cobranza',
  'Consulta y anulación de devoluciones de cobranza a cliente (reintegro de efectivo y impacto en cuenta corriente).',
  {
    queHace:
      'Lista las devoluciones de cobranza (tipo DEV). Podés filtrar por fechas, estado y sucursal, imprimir el PDF y anular una devolución activa cuando la caja y las reglas de negocio lo permitan.',
    pasos: [
      'Filtrá por período, estado (activos/anulados) o sucursal para ubicar el comprobante.',
      'Usá el ícono de PDF para consultar o imprimir la devolución.',
      'Para anular, usá el ícono de bloqueo: el sistema valida primero si se puede (por ejemplo, caja abierta) y pide confirmación.',
    ],
    campos: [
      'Estado: Activo o Anulado según el asentamiento del formulario.',
      'Anular: solo aparece en devoluciones activas; si hay bloqueos, se muestran los motivos del sistema.',
    ],
    errores: [
      'Si la caja o planilla asociada está cerrada, la anulación queda bloqueada hasta reabrirla o según la política definida.',
      'Aplicaciones o movimientos vinculados pueden impedir anular; revisá los motivos que muestra el aviso.',
    ],
    tips: ['El alta de una nueva devolución se hace desde “Nueva devolución” o desde la cuenta corriente del cliente.'],
    links: [
      { label: 'Recibos', to: '/tesoreria/recibos' },
      { label: 'Nueva devolución', to: '/tesoreria/devoluciones/nuevo' },
    ],
  }
);

const helpTesoreriaDevolucionesNuevo = hp(
  'Nueva devolución de cobranza',
  'Alta de una devolución de cobranza: resta saldo en cuenta corriente y egresa efectivo de caja.',
  {
    pasos: [
      'Seleccioná el cliente y la sucursal.',
      'Indicá el importe a reintegrar y el grupo de caja.',
      'Confirmá el medio de reintegro en efectivo y grabá la devolución.',
    ],
    errores: ['Sin cliente, importe o grupo de caja, no se puede registrar la devolución.'],
    links: [{ label: 'Listado de devoluciones', to: '/tesoreria/devoluciones' }],
  }
);

const helpTesoreriaOrdenPago = hp(
  'Órdenes de pago',
  'Instrucciones de pago a proveedores: selección de facturas, retenciones y medios.',
  {
    pasos: [
      'Elegí proveedor y documentos a pagar.',
      'Definí forma de pago y fecha valor.',
      'Si falta o sobra dentro del máximo del MCP de redondeo, usá el botón Redondeo.',
      'Generá la orden y seguí estado hasta ejecutar.',
    ],
    campos: [
      'Medios MCP (efectivo, cheques, propio, etc.).',
      'Redondeo: solo medios MCP con el flag activo. Si falta o sobra dentro del máximo, se cuadra con ese medio.',
    ],
    errores: [
      'Importe mayor al saldo puede estar bloqueado por validaciones.',
      'Sin MCP con redondeo en la caja, el sistema pedirá crear o configurar uno.',
    ],
    tips: [
      'El botón de redondeo aparece cuando la diferencia entra en el máximo configurado en el MCP.',
    ],
    links: [
      { label: 'Facturas compra', to: '/compras/facturas' },
      { label: 'Medios MCP', to: '/tesoreria/medios-mcp' },
    ],
  }
);

const helpTesoreriaMovVarios = hp(
  'Movimientos varios de caja',
  'Ingresos y egresos no ligados a un recibo u orden estándar, según política.',
  {
    pasos: ['Indicá concepto imputable y caja.', 'Verificá signo del importe (entrada/salida).'],
  }
);

const helpTesoreriaMovVariosNuevo = hp(
  'Nuevo movimiento varios',
  'Registro de ingresos o egresos de caja con concepto imputable.',
  {
    pasos: [
      'Seleccioná ingreso o egreso según el movimiento.',
      'Indicá el concepto imputable y la sucursal.',
      'Elegí el grupo de caja y agregá al menos un medio de pago.',
      'Verificá el signo del importe (entrada/salida) antes de registrar.',
    ],
  }
);

const helpTesoreriaMovBancarios = hp(
  'Movimientos bancarios',
  'Registro de movimientos en cuentas bancarias y conciliación parcial.',
  {
    pasos: ['Seleccioná banco y cuenta.', 'Cargá fecha, importe y referencia.'],
    tips: ['Útil junto con conciliación bancaria formal.'],
  }
);

const helpConciliacionBancaria = hp(
  'Conciliación bancaria',
  'Emparejamiento de extracto banco vs movimientos internos.',
  {
    pasos: ['Importá o cargá movimientos del extracto.', 'Marcá coincidencias y diferencias pendientes.'],
    errores: ['Diferencias pueden deberse a cheques no acreditados o errores de fecha valor.'],
  }
);

const helpTablasBancarias = hp(
  'Bancos y tablas de tesorería',
  'Maestros de bancos, carteras, grupos MCP, medios MCP y marcas de tarjeta.',
  {
    pasos: ['Altas desde “Nuevo”.', 'Editá códigos existentes desde la lista.', 'Coordiná con administración antes de eliminar o desactivar.'],
    errores: ['Referencias en movimientos pueden impedir borrar registros.'],
  }
);

const helpMediosMcp = hp(
  'Medios MCP',
  'Alta y mantenimiento de medios de cobro/pago por grupo (caja), incluyendo el medio de redondeo.',
  {
    queHace:
      'Define cada medio MCP (efectivo, tarjeta, cheque, transferencia, etc.) asociado a un grupo/caja, con cartera y parámetros. También marca cuál o cuáles sirven como redondeo.',
    pasos: [
      'Filtrá por tipo, grupo o “Solo redondeo” para ubicar el medio.',
      'En el formulario, completá datos generales y, si aplica, la pestaña Redondeo.',
      'Activá “Usar este MCP como medio de redondeo” e indicá el máximo absoluto.',
    ],
    campos: [
      'Grupo: identifica la caja; cada caja puede tener uno o más MCP de redondeo.',
      'Es redondeo: booleano que habilita el máximo de redondeo.',
      'Máximo de redondeo: tope de la diferencia (falta o sobra) que se puede cuadrar en recibo u orden de pago.',
    ],
    errores: [
      'Si el grupo no tiene ningún MCP con redondeo activo, al operar se pedirá crear o configurar uno.',
      'Si hay varios con el flag activo, el usuario elige cuál usar.',
    ],
    tips: [
      'Podés crear un medio rápido con ?esRedondeo=1&grupo=N desde el diálogo de configuración.',
      'No hace falta que el código se llame “REDONDEO”: alcanza con el flag EsRedondeo.',
    ],
    links: [
      { label: 'Grupos MCP', to: '/tesoreria/grupos-mcp' },
      { label: 'Recibos', to: '/tesoreria/recibos' },
      { label: 'Órdenes de pago', to: '/tesoreria/orden-pago' },
    ],
  }
);

const helpPlanillaCaja = hp(
  'Planilla de caja',
  'Vista operativa de movimientos de caja y posiciones por usuario/fecha.',
  {
    pasos: ['Filtrá por fecha y sucursal o usuario si existe.', 'Revisá ingresos y egresos del día.'],
    tips: ['Útil como cierre diario de caja.'],
  }
);

const helpDisponibilidades = hp(
  'Disponibilidades',
  'Saldo disponible por cuenta bancaria o caja para decidir pagos.',
  {
    pasos: ['Seleccioná entidad financiera.', 'Interpretá saldos contables vs extracto.'],
  }
);

const helpTesoreriaTransferencias = hp(
  'Traspasos (tesorería)',
  'Traspasos entre cuentas propias o instrumentadas desde tesorería.',
  {
    pasos: ['Definí origen, destino e importe.', 'Verificá fecha valor.'],
  }
);

const helpTesoreriaInformesHub = hp(
  'Informes de tesorería',
  'Cheques de terceros y propios, movimientos de carteras y reportes relacionados.',
  {
    pasos: ['Elegí el tipo de informe.', 'Aplicá filtros de fecha y cartera.'],
  }
);

const helpInformeCheques = hp(
  'Informes de cheques',
  'Seguimiento de cheques recibidos, propios y estado de acreditación.',
  {
    pasos: ['Filtrá por estado y fechas.', 'Detectá cheques rechazados o pendientes.'],
  }
);

const helpMovimientosCarterasInforme = hp(
  'Movimientos de carteras',
  'Detalle de movimientos por cartera de valores o cheques.',
  {
    pasos: ['Seleccioná cartera y período.', 'Exportá si necesitás trabajo externo.'],
  }
);

const helpSistemaUsuarios = hp(
  'Usuarios del tenant',
  'Administración de usuarios vinculados al tenant en el que estás trabajando: alta, roles y accesos dentro de esa empresa. No administra identidades globales ni asignaciones a otros tenants (eso corresponde a Usuarios maestros, solo SUPERADMIN).',
  {
    pasos: [
      'Creá el usuario con identificador único dentro del tenant.',
      'Asigná el rol según la política de seguridad de la empresa.',
      'Configurá ubicaciones y grupos de caja en las pestañas del formulario si aplica.',
      'Forzá cambio de contraseña cuando corresponda.',
    ],
    errores: [
      'Usuario duplicado o contraseña que no cumple política serán rechazados.',
      'Solo verás y gestionarás usuarios del tenant actual; si falta alguien, puede no estar asignado a este tenant.',
    ],
    tips: [
      'No compartas credenciales; usá usuario personal.',
      'Para crear la identidad global y vincularla a varios tenants, usá Usuarios maestros.',
    ],
  }
);

const helpSistemaTipoFormulario = hp(
  'Tipos de formulario',
  'Parametrización de tipos de comprobantes y secuencias asociadas.',
  {
    pasos: ['Revisá puntos de venta, letras y numeración.', 'Impacta en todos los circuitos que usan ese tipo.'],
    errores: ['Cambios en uso pueden afectar comprobantes futuros; coordiná con administración.'],
  }
);

const helpSistemaGeografia = hp(
  'Provincias y localidades',
  'Tablas geográficas para direcciones y datos fiscales.',
  {
    pasos: ['Mantené códigos alineados con estándares que use la empresa.', 'Evitá duplicar localidades.'],
  }
);

const helpSistemaMonedas = hp(
  'Cotizaciones de moneda',
  'Carga y mantenimiento de cotizaciones para operaciones en moneda extranjera.',
  {
    queHace:
      'Permite registrar cotizaciones por moneda y fecha (también a futuro) y consultar el historial. En ventas, si falta la cotización del día del comprobante, el sistema pide cargarla una sola vez.',
    pasos: [
      'Elegí la moneda, la fecha y el valor de cotización.',
      'Podés precargar fechas futuras para anticipar operaciones.',
      'Desde Monedas, el botón de cotizaciones abre este listado filtrado por esa moneda.',
    ],
    errores: [
      'Sin cotización del día para la fecha del comprobante, las ventas en moneda extranjera solicitan registrarla antes de continuar.',
    ],
    tips: [
      'Una vez registrada la cotización del día, no se vuelve a pedir en otras transacciones de esa misma fecha.',
      'Si no hay cotización exacta del día, algunos flujos pueden usar la última disponible como respaldo.',
    ],
  }
);

const helpSistemaTiposMoneda = hp(
  'Monedas',
  'Alta y mantenimiento de tipos de moneda (código, descripción, símbolo y datos FCE).',
  {
    pasos: [
      'Definí un código único entre 1 y 255.',
      'Completá descripción y símbolo para listas y precios.',
      'Usá la acción Cotizaciones en cada fila para ver o agregar cotizaciones de esa moneda.',
    ],
    tips: ['Las cotizaciones por fecha se gestionan en Cotizaciones de moneda.'],
  }
);

const helpSistemaCompanias = hp(
  'Compañías (fiscal)',
  'Datos de sociedades o unidades de negocio para facturación y reportes.',
  {
    pasos: ['Completá CUIT y condición IVA.', 'Relacioná con puntos de venta y tipos de formulario.'],
  }
);

const helpSistemaCertificadosArca = hp(
  'Certificados ARCA',
  'Alta y mantenimiento de certificados, claves privadas y servicios web WSFE por compañía.',
  {
    pasos: [
      'Elegí la compañía emisora.',
      'Verificá que la compañía tenga CUIT cargado en el maestro.',
      'Cargá o renová el certificado y la clave privada.',
      'Verificá vigencia y probá el acceso a ARCA.',
    ],
    errores: [
      'Si la compañía no tiene CUIT, el panel avisa y no se pueden consultar ni autorizar comprobantes en AFIP hasta completarlo en Compañías.',
    ],
    tips: ['Las configs WSFE sin compañía (por ejemplo homologación vieja) aparecen al pie: podés editarlas para asignarlas a una compañía o eliminarlas.'],
  }
);

const helpSistemaWsfeTipComp = hp(
  'Tipos de comprobante ARCA (WSFE_TipComp)',
  'Mapeo entre tipo de formulario, letra y código de comprobante AFIP usado al autorizar CAE.',
  {
    pasos: [
      'Indicá tipo de formulario y letra (A, B, C, etc.).',
      'Completá el código AFIP (001 Factura A, 006 Factura B, 011 Factura C, etc.).',
    ],
    errores: ['Si falta el mapeo, la autorización de CAE falla al no encontrar el código AFIP.'],
  }
);

const helpSistemaCajaUsuario = hp(
  'Caja por usuario',
  'Asignación de cajas y grupos MCP a usuarios para operar en tesorería.',
  {
    pasos: ['Asociá usuario con grupo o caja permitida.', 'Verificá restricciones por sucursal.'],
  }
);

const helpConfigTransportes = hp(
  'Transportes',
  'Maestro de transportes/expresos para remitos y logística.',
  {
    pasos: ['Alta y edición de transportistas.', 'Usá códigos consistentes con documentación.'],
  }
);

const helpConfigImpuestos = hp(
  'Impuestos',
  'Definición de impuestos, tasas y reglas de cálculo.',
  {
    pasos: ['Revisá alícuotas vigentes.', 'Coordiná cambios con compras y ventas.'],
    errores: ['Errores de tasa impactan IVA y percepciones.'],
  }
);

const helpConfigUbicaciones = hp(
  'Ubicaciones / depósitos',
  'Depósitos físicos y lógicos para stock y movimientos.',
  {
    pasos: ['Definí códigos de depósito claros.', 'Marcá cuáles intervienen en venta o compra.'],
  }
);

const helpConfigSucursales = hp(
  'Sucursales de venta',
  'Parametrización de puntos de venta y series asociadas.',
  {
    pasos: [
      'Relacioná sucursal con tipos de formulario y numeración.',
      'En Tipos de formulario por Sucursal, solapa Configuración, asigná PassAnulacion / PassEmision con un Id del catálogo Passwords.',
      'Verificá permisos por usuario.',
    ],
    campos: [
      'PassAnulacion: IdPassword requerido para anular comprobantes de ese tipo en esa sucursal.',
      'PassEmision: IdPassword opcional asociado a la emisión.',
    ],
  }
);

const helpConfigModulos = hp(
  'Módulos',
  'Biblioteca de funcionalidades opcionales. Activa o desactiva módulos según el plan de la empresa.',
  {
    queHace:
      'Cada tarjeta resume un módulo. Al hacer clic se abre el detalle con la descripción, los procesos y las entidades. El interruptor lo habilita o lo deshabilita para la empresa.',
    pasos: [
      'Buscá por nombre o filtrá por habilitados y deshabilitados.',
      'Hacé clic en la tarjeta para ver el detalle y el material descargable.',
      'Usá el interruptor o el botón del detalle para habilitar o deshabilitar.',
    ],
    campos: [
      'Costo mensual y logo: solo un superadmin los ve y los edita, dentro del detalle del módulo.',
    ],
    tips: [
      'Al deshabilitar un módulo se ocultan sus pantallas y quedan inactivos los permisos asociados.',
    ],
  }
);

const helpConfigParametros = hp(
  'Parámetros del sistema',
  'Valores de configuración de la empresa. Solo se pueden editar valor y comentario; el alta se realiza desde el backend.',
  {
    pasos: ['Buscá por sección o texto libre.', 'Editá el valor según la documentación del parámetro.'],
    errores: ['Cambios incorrectos pueden afectar ventas, tesorería o cálculos fiscales.'],
  }
);

const helpConfigTokens = hp(
  'Tokens de integración',
  'Administración de accesos para integraciones externas (APIs, servicios).',
  {
    pasos: ['Creá tokens con alcance mínimo necesario.', 'Revocá tokens comprometidos de inmediato.'],
    errores: ['Exponer un token equivale a dar acceso a datos; manejalo como contraseña.'],
  }
);

const helpConfigClavesAgente = hp(
  'Claves de agente (LLM)',
  'Claves personales para conectar OpenClaw, Claude Cowork, DeepSeek u otros agentes al ERP vía MCP/REST.',
  {
    queHace:
      'Cada clave actúa como tu usuario: solo ve los tenants y permisos que tenés. El Bearer se muestra una sola vez; podés revocarlo en cualquier momento.',
    pasos: [
      'Creá una clave con nombre y vencimiento (máx. 90 días).',
      'Copiá el Bearer y el snippet MCP; guardalos en variables de entorno.',
      'Configurá el agente con la URL /mcp y Authorization Bearer.',
      'Si necesitás otra empresa, usá el header X-Tenant-Id con un tenant al que tengas acceso.',
    ],
    errores: [
      'Si el agente recibe 403, faltan permisos de negocio o el módulo CRM no está habilitado.',
      'Una clave revocada o vencida responde 401.',
    ],
    tips: ['No compartas la clave: es tan sensible como tu contraseña.'],
  }
);

const helpConfigPasswords = hp(
  'Contraseñas de autorización',
  'Catálogo de claves hasheadas (tabla Passwords) para autorizar emisión o anulación de comprobantes.',
  {
    queHace:
      'Permite crear y renovar contraseñas de autorización (solo se guarda el hash). El Id se asigna después en Tipos de formulario por Sucursal (PassAnulacion / PassEmision).',
    pasos: [
      'Creá una contraseña con un Id (hasta 20 caracteres) y una clave de al menos 12 caracteres.',
      'En Configuraciones → Tipos de formulario por Sucursal, solapa Configuración, elegí ese Id en PassAnulacion (o PassEmision).',
      'Al anular un remito (u otro comprobante que lo exija), el usuario ingresa la clave en claro de ese Id.',
    ],
    campos: [
      'Id: identificador de Passwords (IDPASSWORD); es lo que guarda SucVenta.PassAnulacion / PassEmision.',
      'Clave: valor en claro solo al crear o cambiar; nunca se vuelve a mostrar.',
    ],
    errores: [
      'No se puede eliminar un Id referenciado en SucVenta: primero cambiá o vaciá PassAnulacion / PassEmision.',
    ],
    tips: [
      'Usá Ids descriptivos (ej. ANULAR_REMITOS) y rotá la clave sin cambiar el Id si ya está asignado en SucVenta.',
    ],
    links: [{ label: 'Tipos de formulario por Sucursal', to: '/configuraciones/sucventa' }],
  }
);

const helpConfigWebhooks = hp(
  'Webhooks',
  'Notificaciones HTTP que eOne envía a tu sistema cuando ocurren eventos de negocio (HMAC, reintentos y logs).',
  {
    queHace:
      'Permite crear suscripciones outbound: URL de destino, eventos, secret HMAC, filtros, reintentos, timeout, headers y alerta por mail. Desde el listado podés probar, ver logs y reintentar entregas fallidas.',
    pasos: [
      'Creá un webhook con URL HTTPS y al menos un evento.',
      'Si tu sistema debe validar autenticidad, cargá un secret HMAC.',
      'Usá Probar para enviar un POST de prueba y revisá logs en el detalle.',
      'Si un envío queda failed, reintentalo desde el detalle.',
    ],
    campos: [
      'URL: endpoint que recibe POST JSON.',
      'Secret: firma HMAC-SHA256 del body crudo (header X-Webhook-Signature).',
      'Filtros: reglas AND/OR para no disparar la URL si el documento no cumple.',
      'Máx. reintentos y timeout: controlan la entrega automática.',
    ],
    errores: [
      'Sin URL o sin eventos no se guarda.',
      'Si la firma no valida en tu lado, verificá el body crudo (no el JSON re-serializado).',
      'HTTP distinto de 2xx o timeout genera reintentos y, al agotarlos, estado failed.',
    ],
    tips: [
      'Respondé 2xx apenas aceptás el evento y procesá en segundo plano.',
      'La documentación técnica completa está en esta misma sección: Documentación.',
    ],
    links: [{ label: 'Documentación técnica de webhooks', to: '/configuraciones/webhooks/ayuda' }],
  }
);

const helpConfigWebhooksDocs = hp(
  'Documentación de webhooks',
  'Guía técnica para integrar notificaciones HTTP outbound de eOne: payload, HMAC-SHA256, eventos, reintentos y ejemplos.',
  {
    queHace:
      'Explica cómo recibir, verificar y responder los POST que eOne envía a tu URL, con el mismo alcance que la documentación del API.',
    pasos: [
      'Leé Introducción y el flujo general para ubicar el modelo (eOne llama a tu sistema).',
      'Configurá la suscripción en Webhooks y, si aplica, el secret HMAC.',
      'Implementá la verificación de X-Webhook-Signature sobre el body crudo.',
      'Respondé HTTP 2xx y usá Probar / logs para validar la entrega.',
    ],
    campos: [
      'Firma: HMAC-SHA256 hex con prefijo sha256=.',
      'Éxito de entrega: cualquier HTTP 2xx.',
      'Backoff exponencial hasta maxReintentos (default 3).',
    ],
    tips: ['Volvé a la pantalla de Webhooks para crear o probar una suscripción.'],
    links: [{ label: 'Configuración de webhooks', to: '/configuraciones/webhooks' }],
  }
);

const helpContabilidadImputables = hp(
  'Cuentas imputables',
  'Plan de cuentas imputables para movimientos contables detallados.',
  {
    pasos: ['Mantené códigos sin duplicar.', 'Verificá naturaleza de cuenta antes de asignar a documentos.'],
  }
);

const helpContabilidadNoImputables = hp(
  'Rubros no imputables',
  'Agrupadores de cuentas para reportes y asientos.',
  {
    pasos: ['Usá rubros consistentes con el plan contable interno.'],
  }
);

const helpContabilidadEstrategia = hp(
  'Estrategia contable',
  'Definición de estrategias o esquemas de imputación contable: cuentas por módulo usadas al generar asientos.',
  {
    queHace:
      'Asocia cada concepto (clientes, IVA, ajuste por inflación, etc.) a una cuenta imputable del plan.',
    pasos: [
      'Completá nombre, tipo y descripción de la estrategia.',
      'En cada solapa (Compras, Ventas, Tesorería, Otros) asigná la cuenta con el buscador de cada campo.',
      'Usá el buscador de campos (arriba a la derecha) para ubicar conceptos como “ajuste x inflación”.',
      'Guardá; la estrategia 1 aporta la cuenta RECPAM al ajuste por inflación.',
    ],
    campos: [
      'Ajuste por inflación: cuenta de resultado por exposición a la inflación (RECPAM); está en Otros o buscala por nombre.',
    ],
    tips: ['Revisá parámetros antes de aplicar a nuevos movimientos y documentá cambios para auditoría.'],
  }
);

const helpContabilidadLibroDiario = hp(
  'Asientos del libro diario',
  'Alta y seguimiento de asientos manuales (LibroDiario + DeLiDi). El total debe debe igualar al total haber. Contabilizar bloquea ediciones; solo SUPERADMIN puede descontabilizar.',
  {
    pasos: [
      'Creá líneas con cuenta imputable, detalle breve e importe en debe o en haber.',
      'Verificá que el asiento esté balanceado antes de guardar.',
      'Usá Contabilizar cuando el asiento esté listo; no podrás editarlo hasta descontabilizar (SUPERADMIN).',
    ],
    errores: ['No se eliminan asientos vinculados a otros módulos ni los ya contabilizados.'],
  }
);

const helpContabilidadLibroMayor = hp(
  'Libro mayor',
  'Consulta de movimientos por cuenta imputable en un rango de fechas: saldo inicial (movimientos anteriores al período, solo en la primera página), líneas paginadas del libro diario y saldo acumulado por línea (debe menos haber). Podés abrir cada asiento desde la columna Ver.',
  {
    pasos: [
      'Seleccioná la cuenta imputable y el período (fechas inclusivas como en libro diario).',
      'Pulsá Consultar para cargar movimientos y saldos.',
      'Libro mayor abre vista previa PDF con todos los movimientos del período; Exportar Excel descarga el mismo detalle en .xlsx.',
      'Usá la paginación inferior para recorrer muchos movimientos en pantalla; el saldo de cada línea respeta el orden cronológico completo.',
      'Usá Ver para revisar el asiento completo en libro diario.',
    ],
    errores: ['Rangos muy amplios pueden superar el límite de líneas del servidor; acotá fechas.'],
  }
);

const helpContabilidadSumasYSaldos = hp(
  'Sumas y Saldos',
  'Balance de comprobación del plan de cuentas: movimientos del período en Debe/Haber y saldos deudor/acreedor, con saldo inicial por cuenta.',
  {
    queHace:
      'Lista cuentas imputables con movimiento o saldo previo, agrupadas por rubros del plan, mostrando saldo inicial, sumas del período y saldo final en columna deudora o acreedora.',
    pasos: [
      'Elegí el período con los chips o fechas desde/hasta.',
      'Pulsá Cargar para ver el detalle en pantalla.',
      'Usá PDF para la vista de impresión o Exportar Excel para el mismo detalle en planilla.',
    ],
    campos: [
      'Fecha desde / hasta: delimitan el período de movimientos; el saldo inicial acumula asientos anteriores a la fecha desde.',
      'Debe / Haber: sumas de movimientos del período.',
      'Saldo Deudor / Acreedor: saldo neto (inicial + Debe − Haber); queda en una sola columna según el signo.',
    ],
    tips: [
      'Los rubros no imputables aparecen como encabezados sin importes.',
      'Solo se listan cuentas con movimiento en el período o con saldo inicial distinto de cero.',
    ],
  }
);

const helpAjusteInflacion = hp(
  'Ajuste por inflación',
  'Generación del asiento de ajuste por inflación a partir de índices IPC y cuentas clasificadas en el plan de cuentas.',
  {
    queHace:
      'Calcula la reexpresión a moneda de cierre del período, muestra el papel de trabajo y permite generar el asiento de RECPAM contra la cuenta de resultado por exposición a la inflación de la estrategia 1.',
    pasos: [
      'En Índices, cargá cada mes como nivel absoluto del IPC o como variación %; el sistema guarda siempre el nivel.',
      'En Prerrequisitos, resolvé alertas generales y cuentas sin clasificar o mal clasificadas (podés aplicar clase en masa).',
      'En Proceso, revisá el mes de referencia, calculá y revisá el papel de trabajo.',
      'Confirmá Generar asiento para crear el asiento AXINFLACION al cierre del período.',
    ],
    campos: [
      'Forma: Nivel IPC (ej. 102,5) o Variación % del mes (ej. 2,5). La variación se convierte con el mes anterior.',
      'Excel: columnas Año, Mes, Índice y/o Variación %; en cada fila alcanza con una de las dos (si hay ambas, gana el Índice).',
      'Mes de referencia: índice con el que se toma el saldo inicial; por defecto el mes anterior al inicio del rango.',
      'Prerrequisitos: alertas (RECPAM, índices, asiento contabilizado) y listado de cuentas con el problema detectado.',
      'RECPAM: contrapartida del asiento; la cuenta sale de la estrategia 1 y debe clasificarse como No ajusta.',
    ],
    errores: [
      'Sin índices para todos los meses del rango, el cálculo no puede continuar.',
      'Una variación sin nivel del mes anterior (en pantalla o en el Excel) no se puede convertir.',
      'Cuentas sin clasificar, con clase inválida o RECPAM mal clasificada bloquean el cálculo.',
      'Si ya hay un asiento de ajuste contabilizado en el rango, hay que descontabilizarlo antes de generar otro.',
    ],
    tips: [
      'Usá el lápiz de una fila para traer ese mes al formulario y corregirlo; el tacho lo elimina.',
      'En Prerrequisitos filtrá por cuenta o motivo, marcá las clasificables y aplicá la clase; las alertas de índices se resuelven en Índices.',
      'Usá el ícono de ayuda junto a Índices para ver el ejemplo de conversión.',
      'El primer mes de una serie con variación necesita un nivel de base ya cargado.',
      'Importar Excel abre el mismo flujo que productos: descargar plantilla y seleccionar archivo.',
    ],
  }
);

const helpSistemaModulo = hp(
  'Administración del sistema',
  'Configuración de usuarios del tenant, tablas maestras geográficas, monedas, compañías fiscales, tipos de comprobante y cajas por usuario. Los cambios pueden afectar a todos los módulos.',
  {
    pasos: [
      'Verificá permisos antes de modificar datos críticos.',
      'Documentá cambios en tipos de formulario y compañías para el equipo contable.',
    ],
    errores: ['Operaciones sin permiso serán rechazadas por el servidor.'],
  }
);

const helpConfiguracionesModulo = hp(
  'Configuraciones generales',
  'Transportes, impuestos, ubicaciones, sucursales de venta, tokens de integración y webhooks. Suelen ser tablas base para operaciones comerciales, logística e integraciones.',
  {
    pasos: ['Coordiná con administración antes de eliminar registros referenciados.', 'Probá en ambiente de prueba cambios masivos.'],
  }
);

const helpContabilidadModulo = hp(
  'Contabilidad',
  'Libro diario (asientos manuales), libro mayor por cuenta, plan de cuentas imputables, rubros no imputables y estrategias de imputación. Impacta en cómo se reflejan compras, ventas y tesorería en reportes contables.',
  {
    pasos: ['Mantené coherencia con el plan contable externo del estudio.', 'Evitá eliminar cuentas con movimientos históricos.'],
  }
);

const helpPanelGranja = hp(
  'Panel Granja',
  'KPIs operativos de la fecha operativa activa: postura, mortalidad, recolección por lote, ratio de calibres y autonomía de alimento. Accesos rápidos a las pantallas de operación.',
  {
    queHace:
      'Consolida indicadores del día operativo por lote y alertas sanitarias o de stock mínimo. La recolección y las bajas se registran por lote; el artículo de huevo o aves puede ser genérico compartido entre lotes. El gráfico por lote muestra huevos recolectados y % de postura con ejes separados. Los últimos 7 días muestran cantidades de postura y mortandad, y la clasificación por calibre en gráfico y tabla.',
    pasos: [
      'Confirmá la fecha operativa en el chip del encabezado; los KPIs y alertas del día usan esa fecha.',
      'Revisá postura diaria y mortalidad acumulada (se recalculan al filtrar por lote).',
      'Revisá las cantidades de postura (huevos) y mortandad (aves) de los últimos 7 días; el filtro de lote también aplica ahí.',
      'En Clasificación por calibre, el gráfico y la tabla muestran la misma cantidad por día y calibre, con totales.',
      'Tocá una barra del gráfico Cantidades / postura para filtrar los KPIs superiores; tocá de nuevo o cerrá el aviso para ver todos.',
      'Usá los accesos rápidos para recolección, clasificación, aves, alimento, despacho, movimientos o configuración.',
    ],
    campos: [
      'Fecha operativa: define el día de recolección, % postura y bajas/alertas del día.',
      'Recolectado: suma del registro de recolección de ese día por lote (no depende del código de artículo).',
      'Cabezas vivas: cabezas iniciales menos mortandad y descarte acumulados del lote.',
      '% Postura: recolectado ÷ cabezas vivas × 100.',
      'Postura y mortandad — últimos 7 días: los días van en columnas. Filas: huevos, aves muertas, % postura y % mortandad (cada % = cantidad ÷ gallinas vivas de ese día). El total de cada % usa la suma de gallinas de cada día. El descarte no suma a la mortandad.',
      'Ratio de calibres y clasificación 7 días: salen de las clasificaciones registradas. En la tabla los días van en columnas y cada calibre en una fila, con total por día y por calibre.',
    ],
    errores: [
      'Sin recolección en la fecha operativa, recolectado y postura quedan en cero.',
      'Si las cabezas vivas no coinciden con la realidad del lote (bajas anteriores al registro por lote), ajustá Cabezas iniciales en Configuración.',
    ],
    tips: [
      'Al cambiar la fecha operativa, el panel se recarga solo.',
      'Recolección, clasificación, aves, alimento y despacho usan la fecha operativa en los movimientos.',
      'El eje izquierdo es cantidad de huevos; el derecho es % de postura.',
      'Las alertas sanitarias aparecen cuando las bajas del día operativo superan la tolerancia del lote.',
      'Podés usar el mismo artículo de huevo bruto o de aves en todos los lotes: las estadísticas se separan por el registro de lote.',
    ],
    links: [
      { label: 'Recolección', to: '/granja/recoleccion' },
      { label: 'Control de aves', to: '/granja/aves' },
      { label: 'Movimientos', to: '/granja/movimientos' },
      { label: 'Informes', to: '/granja/informes' },
      { label: 'Configuración Granja', to: '/granja/config' },
    ],
  }
);

const helpGranjaRecoleccion = hp(
  'Recolección',
  'Carga de maples y huevos en bruto por lote. Genera ingreso de stock del artículo de huevo bruto y un registro por lote para las estadísticas.',
  {
    queHace:
      'Permite registrar cada ronda de recolección indicando el lote. El stock se mueve sobre el artículo configurado (puede ser genérico); el KPI de postura usa el registro por lote. La fecha del registro es la fecha operativa activa.',
    pasos: [
      'Confirmá la fecha operativa en el encabezado.',
      'Si no hay sucursal operativa, abrí el engranaje junto al título y elegí la sucursal.',
      'Seleccioná el lote.',
      'Ingresá maples y/o huevos sueltos con el teclado numérico.',
      'Confirmá para grabar el ingreso y el registro de recolección.',
    ],
    campos: [
      'Lote: determina a qué galpón/lote se imputa la cantidad en estadísticas.',
      'Maples / huevos: se convierten a unidades de huevo antes de guardar.',
      'Fecha: se toma de la fecha operativa de la sesión.',
      'Sucursal operativa: se configura con el engranaje del título; se usa al registrar la ronda.',
    ],
    errores: [
      'Sin depósito Granja configurado no se puede registrar.',
      'Sin sucursal operativa, la operación se rechaza y se abre el modal para configurarla.',
    ],
    tips: [
      'Varias rondas del mismo día operativo se suman en el acumulado del lote y en el panel.',
      'Si varios lotes comparten el mismo artículo de huevo, igual se distinguen en el panel.',
      'Si tu usuario no tiene sucursal preferida, configurála una vez con el engranaje.',
      'Ver movimientos abre el listado filtrado a recolecciones.',
    ],
    links: [
      { label: 'Panel Granja', to: '/granja' },
      { label: 'Clasificación', to: '/granja/clasificacion' },
      { label: 'Movimientos', to: '/granja/movimientos?tipo=RECOLECCION' },
    ],
  }
);

const helpGranjaInformes = hp(
  'Informes Granja',
  'Informes operativos de granja. El primero es Postura semanal, con el mismo formato del libro de referencia (semanas lunes–domingo por lote y total).',
  {
    queHace:
      'Calcula por semana calendario (lunes a domingo) la cantidad de postura, semanas productivas, muertas, gallinas y % de postura de cada lote seleccionado, más un total agregado. Permite exportar a Excel.',
    pasos: [
      'Elegí el rango Desde / Hasta.',
      'Marcá los lotes a incluir.',
      'Pulsá Generar para ver la grilla.',
      'Usá Exportar Excel para descargar el archivo con el formato del libro.',
    ],
    campos: [
      'Fecha Inicial / Final: tramo de la semana (puede ser parcial al inicio o fin del rango).',
      'Cant. Postura: huevos recolectados del lote en esa semana.',
      'Sem. Prod.: edad del lote en semanas desde la fecha de ingreso hasta el fin del período.',
      'Muertas: mortandad registrada en la semana.',
      'Gallinas: cabezas vivas al cierre del período (iniciales − mortandad − descarte acumulados).',
      '% Postura: huevos ÷ (gallinas × 7) × 100; siempre 7 días aunque falten cargas de recolección en la semana.',
      'TOTAL: suma de postura, muertas y gallinas; el % se recalcula sobre el total.',
    ],
    errores: [
      'Sin lotes activos no se puede generar el informe.',
      'Si faltan recolecciones o bajas en el registro por lote, las columnas quedan en cero o desalineadas.',
    ],
    tips: [
      'El rango por defecto cubre unas 12 semanas hacia atrás desde la fecha operativa.',
      'Ejemplo: 1043 huevos ÷ (498 gallinas × 7) = 29,92 %.',
      'En el Excel el % se exporta como fracción (igual que el libro de referencia) para formatearlo como porcentaje.',
    ],
    links: [
      { label: 'Panel Granja', to: '/granja' },
      { label: 'Recolección', to: '/granja/recoleccion' },
      { label: 'Control de aves', to: '/granja/aves' },
    ],
  }
);

const helpGranjaAves = hp(
  'Control de aves',
  'Mortandad, descarte y alta de lotes. Las bajas se registran por lote y actualizan cabezas vivas y alertas sanitarias del panel.',
  {
    queHace:
      'Registra bajas (mortandad o descarte) asociadas al lote seleccionado, egresa stock del artículo de aves y actualiza las cabezas vivas del lote. La fecha de la baja es la fecha operativa activa. El alta de lote agrega la config y el stock inicial.',
    pasos: [
      'Confirmá la fecha operativa en el encabezado.',
      'En Mortandad o Descarte: elegí el lote, ingresá cantidad y confirmá.',
      'En Alta lote: completá código, elegí artículos por lookup, cabezas iniciales y tolerancia; se guarda la config y el ingreso de stock.',
    ],
    campos: [
      'Cabezas vivas: cabezas iniciales menos mortandad y descarte del registro por lote (no el saldo genérico del artículo).',
      'Tolerancia %: umbral diario para alerta sanitaria en el panel.',
      'Artículo aves / huevo bruto: se eligen con el mismo lookup que en Configuración Granja (código o búsqueda); pueden ser genéricos compartidos entre lotes.',
      'Fecha de baja: fecha operativa de la sesión.',
    ],
    errores: [
      'La baja no puede superar las cabezas vivas del lote ni el saldo del depósito Granja.',
      'Si el lote ya tenía bajas antes del registro por lote, ajustá Cabezas iniciales en Configuración para alinear el saldo lógico.',
    ],
    tips: [
      'Usá el mismo artículo de aves en todos los lotes si el stock físico es común; las estadísticas siguen por lote.',
      'Después de ajustar cabezas iniciales, volvé a abrir Control de aves o el Panel para ver el valor actualizado.',
      'Ver movimientos abre el listado filtrado según la pestaña activa (Mortandad, Alta lote o Descarte).',
    ],
    links: [
      { label: 'Panel Granja', to: '/granja' },
      { label: 'Configuración Granja', to: '/granja/config' },
      { label: 'Movimientos', to: '/granja/movimientos' },
    ],
  }
);

const helpGranjaConfig = hp(
  'Configuración Granja',
  'Parámetros operativos del módulo: depósito, batch del mixer, receta por defecto, lotes de aves y artículos de clasificación.',
  {
    queHace:
      'Guarda depósito Granja, kilos del batch, artículo de cascado, receta del mixer, lotes activos y el mapeo calibre/color a artículo. Esos valores los usan recolección, clasificación, aves y alimento.',
    pasos: [
      'Completá depósito, batch y artículo de cascado.',
      'Elegí la receta del mixer con el buscador (código o descripción).',
      'Cargá los lotes con artículos de aves y huevo bruto, fecha de ingreso, cabezas y tolerancia.',
      'Definí los artículos de clasificación por calibre y color.',
      'Guardá para aplicar la configuración.',
    ],
    campos: [
      'Depósito: ubicación de stock de la granja.',
      'Batch kg: kilos por defecto al elaborar alimento.',
      'Cascado: artículo al que ingresa el huevo roto o sucio.',
      'Receta mixer: fórmula por defecto. Se busca por código o descripción y se puede abrir el listado de recetas.',
      'Lote: código, artículos, ingreso, cabezas iniciales, tolerancia % y si está activo.',
      'Clasificación: calibre, color y artículo de stock resultante.',
    ],
    errores: [
      'Si la receta escrita no existe, el campo avisa y no queda resuelta.',
      'Los lotes sin código y los artículos de clasificación vacíos no se guardan.',
    ],
    tips: [
      'La lupa o F4 abre el buscador de recetas.',
      'Podés usar el mismo artículo de aves o de huevo bruto en varios lotes.',
    ],
    links: [
      { label: 'Panel Granja', to: '/granja' },
      { label: 'Alimento / Mixer', to: '/granja/alimento' },
    ],
  }
);

const helpGranjaAlimento = hp(
  'Alimento / Mixer',
  'Elabora alimento según una receta y descuenta las materias primas del depósito Granja.',
  {
    queHace:
      'Toma la receta (por defecto la del mixer configurada), arma el preview de consumos y, si hay stock, confirma la producción del día operativo.',
    pasos: [
      'Elegí la receta con el buscador o dejá la receta por defecto de Configuración.',
      'Indicá los kilos a elaborar (por defecto el batch configurado).',
      'Pulsá Ver consumos para revisar insumos requeridos y disponibles.',
      'Si el stock alcanza, pulsá Elaborar.',
    ],
    campos: [
      'Receta: se busca por código o descripción. La lupa abre el listado de recetas.',
      'Kg a elaborar: cantidad del producto terminado de la receta.',
      'Consumos: artículo, cantidad requerida y disponible en el depósito Granja.',
    ],
    errores: [
      'Sin depósito Granja configurado no se puede elaborar.',
      'La receta tiene que existir y tener producto terminado.',
      'Si algún insumo que controla stock no alcanza, Elaborar se rechaza.',
    ],
    tips: [
      'La receta y los kilos iniciales salen de Configuración Granja.',
      'Al cambiar la receta se descarta el preview anterior.',
      'La fecha del movimiento es la fecha operativa activa.',
      'Ver movimientos abre el listado de comprobantes de stock Granja (el mixer usa el circuito de producción).',
    ],
    links: [
      { label: 'Panel Granja', to: '/granja' },
      { label: 'Configuración Granja', to: '/granja/config' },
      { label: 'Movimientos', to: '/granja/movimientos' },
    ],
  }
);

const helpGranjaClasificacion = hp(
  'Clasificación',
  'Convierte huevo bruto del depósito Granja en stock por calibre/color, cascado y merma (tirado).',
  {
    queHace:
      'Egresa el bruto por FIFO de lotes activos (sin elegir lote) e ingresa vendibles y cascado. El tirado se registra como merma sin impacto de stock. No se puede clasificar más que la cantidad bruta; si clasificás menos, el restante queda en stock.',
    pasos: [
      'Confirmá la fecha operativa en el encabezado.',
      'Revisá o ajustá la cantidad bruta (por defecto el saldo FIFO de huevo bruto en Granja).',
      'Ingresá maples y/o huevos por calibre/color, cascado y tirado.',
      'Si la suma supera la bruta, corregí antes de confirmar. Si es menor, podés confirmar igual.',
      'Confirmá para grabar egreso, ingreso y el registro por lote FIFO.',
    ],
    campos: [
      'Cantidad bruta: techo sugerido (saldo agregado de artículos bruto de lotes activos).',
      'Calibres / colores: artículos de Configuración Granja; maples y huevos sueltos.',
      'Cascado / roto / sucio: ingresa al artículo de cascado configurado.',
      'Tirado: merma sin movimiento de stock.',
      'Pendiente: diferencia bruto − clasificado; si es positiva al confirmar, queda saldo.',
    ],
    errores: [
      'No se admite clasificar más que la cantidad bruta.',
      'Sin depósito Granja o sin artículos de clasificación configurados no se puede operar.',
      'Sin sucursal operativa asignada al usuario, la operación se rechaza.',
      'Si no hay stock suficiente de huevo bruto (FIFO), el egreso falla.',
    ],
    tips: [
      'El bruto se consume por orden de ingreso de lotes (FIFO), sin seleccionar lote en pantalla.',
      'Varias clasificaciones del mismo día se reflejan en el panel y en los informes de granja.',
      'Ver movimientos abre el listado filtrado a clasificaciones.',
    ],
    links: [
      { label: 'Panel Granja', to: '/granja' },
      { label: 'Configuración Granja', to: '/granja/config' },
      { label: 'Recolección', to: '/granja/recoleccion' },
      { label: 'Movimientos', to: '/granja/movimientos?tipo=CLASIFICACION' },
    ],
  }
);

const helpGranjaMovimientos = hp(
  'Movimientos Granja',
  'Listado de comprobantes de stock del módulo: recolección, clasificación, mortandad, descarte, alta de lote y despacho de producción.',
  {
    queHace:
      'Muestra los formularios de ajuste/transferencia generados por las pantallas operativas de Granja, con filtros por fecha, tipo y búsqueda. Desde Ver se abre el comprobante en inventario.',
    pasos: [
      'Ajustá el rango de fechas y, si querés, el tipo de movimiento.',
      'Usá Buscar para filtrar por comprobante, descripción o lote.',
      'Tocá Ver para abrir el detalle del ajuste/transferencia.',
    ],
    campos: [
      'Tipo: motivo del comprobante (recolección, clasificación, etc.).',
      'Lote: aparece cuando el movimiento quedó vinculado en el registro por lote.',
      'Comprobante: letra-sucursal-número (ej. X-00001-00000042).',
    ],
    errores: [
      'Sin resultados en el rango, ampliá las fechas o quitá el filtro de tipo.',
    ],
    tips: [
      'Desde cada pantalla operativa, Ver movimientos abre este listado ya filtrado cuando corresponde.',
      'Los despachos históricos sin motivo DESPACHO_PROD siguen apareciendo por la descripción «Despacho prod».',
    ],
    links: [
      { label: 'Panel Granja', to: '/granja' },
      { label: 'Recolección', to: '/granja/recoleccion' },
      { label: 'Clasificación', to: '/granja/clasificacion' },
    ],
  }
);

const helpComprobanteInternoPos = hp(
  'Crédito / débito interno (POS)',
  'Alta de comprobante interno 80/81 con interfaz POS: tercero, renglones y sucursal.',
  {
    queHace:
      'Emite un crédito o débito interno (letra X, TipoIva No Categorizado) con asiento e ítems, sin AFIP.',
    pasos: [
      'Elegí cliente o proveedor.',
      'Buscá y cargá artículos financieros (tipo FIN); cantidad y precio.',
      'Revisá sucursal, fechas y observaciones (Tipo IVA queda en No categorizado).',
      'Guardá el comprobante; la impresión queda disponible desde el listado.',
    ],
    campos: [
      'Letra X y numerador FormuN de SucVenta para el tipo 80/81.',
      'Tipo IVA fijo: No categorizado (no editable).',
      'Solo artículos de tipo Financiero (FIN).',
      'Sin discriminación de IVA: el total de líneas = importe del comprobante y del asiento.',
      'Cuenta contable del renglón: CtaVenta (clientes) o CtaCompra (proveedores).',
    ],
    errores: [
      'Falta SucVenta/FormuN para la sucursal y tipo 80/81.',
      'Artículo sin cuenta contable configurada.',
      'Artículos que no sean FIN no se pueden agregar.',
    ],
    tips: [
      'Volvé a Movimientos internos (Ventas o Compras) con el botón de atrás.',
      'La impresión del PDF queda disponible desde el listado.',
    ],
  }
);

/** Reglas con regex o condiciones especiales (se evalúan primero). */
export const HELP_SPECIAL_RULES: HelpRouteRule[] = [
  {
    id: 'factura-venta-form',
    match: (p) => {
      if (p === ROUTES.ventas.facturas.nuevo || p === ROUTES.ventas.facturas.pos) return true;
      if (p.startsWith(`${ROUTES.ventas.facturas.pos}?`)) return true;
      return /^\/ventas\/facturas\/[^/]+\/(ver|editar)$/.test(p);
    },
    content: helpFacturaVenta,
  },
  {
    id: 'nota-credito-compra-nuevo',
    match: (p) => p === '/compras/notas-credito/nuevo',
    content: helpNotaCreditoCompra,
  },
  {
    id: 'lote-detalle',
    match: (p) => /^\/inventario\/lotes\/\d+$/.test(p),
    content: helpLotesDetalle,
  },
  {
    id: 'comprobante-interno-pos',
    match: (p) =>
      /\/ventas\/movimientos-internos\/(credito|debito)\/nuevo$/.test(p) ||
      /\/compras\/movimientos-internos\/(credito|debito)\/nuevo$/.test(p) ||
      /\/compras\/informes\/cuentas-pagar\/(credito|debito)-interno\/nuevo$/.test(p),
    content: helpComprobanteInternoPos,
  },
];

/** Rutas exactas con guía dedicada (sin ambigüedad con prefijos). */
export const HELP_EXACT: Record<string, PageHelpContent> = {
  '/dashboard': helpDashboard,
  '/menu-principal': helpMenuPrincipal,
  '/granja': helpPanelGranja,
  '/granja/recoleccion': helpGranjaRecoleccion,
  '/granja/clasificacion': helpGranjaClasificacion,
  '/granja/movimientos': helpGranjaMovimientos,
  '/granja/informes': helpGranjaInformes,
  '/granja/aves': helpGranjaAves,
  '/granja/config': helpGranjaConfig,
  '/granja/alimento': helpGranjaAlimento,
  '/ventas': helpModuloVentas,
  '/ventas/facturas': helpFacturasListadoVentas,
  '/ventas/contratos': helpContratosVenta,
  '/ventas/contratos/facturacion': helpContratosFacturacion,
  '/compras': helpModuloCompras,
  '/inventario': helpModuloInventario,
  '/tesoreria': helpModuloTesoreria,
  '/ventas/consultas-ia': helpConsultaIa('ventas'),
  '/compras/consultas-ia': helpConsultaIa('compras'),
  '/inventario/consultas-ia': helpConsultaIa('inventario'),
  '/tesoreria/consultas-ia': helpConsultaIa('tesorería'),
  '/inventario/lotes/numeracion': helpLotesNumeracion,
  '/configuraciones/modulos': helpConfigModulos,
  '/ventas/movimientos-internos': helpMovimientosInternosCxc,
  '/compras/movimientos-internos': helpMovimientosInternosCxp,
};

/**
 * Prefijos de mayor a menor longitud al resolver (se ordena en runtime).
 * Cubre formularios, listados y detalles bajo la misma guía temática.
 */
const PREFIX_ENTRIES: [string, PageHelpContent][] = [
  ['/ventas/informes/seguimiento-ventas', helpInformeSeguimientoVentas],
  ['/ventas/movimientos-internos', helpMovimientosInternosCxc],
  ['/ventas/informes/cuentas-cobrar', helpInformeCuentasCobrar],
  ['/compras/movimientos-internos', helpMovimientosInternosCxp],
  ['/ventas/informes/resumen-cuenta', helpInformeResumenCuentaVentas],
  ['/ventas/informes/libro-iva-ventas', helpInformeLibroIvaVentas],
  ['/ventas/informes/ventas-dinamico', helpInformeVentasDinamico],
  ['/ventas/informes/ventas-por-producto', helpInformeVentasPorProducto],
  ['/ventas/informes/ventas-resultados', helpInformeVentasResultados],
  ['/ventas/informes/personalizados', helpInformesPersonalizados],
  ['/ventas/informes', helpVentasInformesHub],
  ['/ventas/cotizaciones', helpPedidosCotizaciones('Cotizaciones')],
  ['/ventas/pedidos', helpPedidosCotizaciones('Pedidos')],
  ['/ventas/picking-masivo', helpPickingMasivo],
  ['/ventas/remitos', helpRemitosVenta],
  ['/ventas/notas-credito', helpNotasCreditoVenta],
  ['/ventas/clientes', helpMaestroCliente],
  ['/ventas/vendedores', helpVentasMaestrosAux],
  ['/ventas/rutas', helpVentasMaestrosAux],
  ['/ventas/visitas', helpVentasMaestrosAux],
  ['/ventas/contratos', helpContratosVenta],
  ['/ventas/listaprecios', helpVentasMaestrosAux],
  ['/ventas/lista-precios', helpVentasMaestrosAux],
  ['/ventas/articuloxlistaprecio', helpArticuloListaPrecio],
  ['/compras/informes/compras-dinamico', helpInformeComprasDinamico],
  ['/compras/informes/seguimiento-compras', helpSeguimientoCompras],
  ['/compras/informes/cuentas-pagar', helpInformeCuentasPagar],
  ['/compras/informes/resumen-cuenta', helpComprasInformesHub],
  ['/compras/informes/libro-iva-compras', helpComprasInformesHub],
  ['/compras/informes/personalizados', helpInformesPersonalizados],
  ['/compras/informes', helpComprasInformesHub],
  ['/compras/facturas', helpComprasFacturas],
  ['/compras/ordenes', helpComprasOrdenes],
  ['/compras/remitos', helpComprasRemitos],
  ['/compras/proveedores', helpProveedores],
  ['/compras/ramosprov', helpRamosProv],
  ['/compras/notas-credito', helpNotasCreditoCompraLista],
  ['/inventario/informes/stock-deposito', helpStockPorDeposito],
  ['/inventario/informes/stock-a-fecha', helpInventarioInformes],
  ['/inventario/informes/stock-inmovilizado', helpInventarioInformes],
  ['/inventario/informes/movimientos-stock', helpMovimientosStock],
  ['/inventario/informes/personalizados', helpInformesPersonalizados],
  ['/inventario/informes', helpInventarioInformes],
  ['/inventario/descuentos', helpDescuentos],
  ['/inventario/productos', helpInventarioProductos],
  ['/inventario/transferencias', helpInventarioTransferencias],
  ['/inventario/ramos', helpInventarioClasificacion],
  ['/inventario/lineas', helpInventarioClasificacion],
  ['/inventario/clases', helpInventarioClasificacion],
  ['/inventario/unidades-medida', helpUnidadesMedida],
  ['/ventas/ordenes-devolucion', helpOrdenesDevolucion],
  ['/inventario/devoluciones', helpInventarioOperativo],
  ['/inventario/produccion', helpInventarioOperativo],
  ['/produccion/programacion/preview', helpProgramacionProduccionPreview],
  ['/produccion/programacion', helpProgramacionProduccion],
  ['/produccion', helpInventarioOperativo],
  ['/produccion/recetas', helpRecetasProduccion],
  ['/produccion/ordenes', helpOrdenesProduccion],
  ['/inventario/picking', helpInventarioOperativo],
  ['/inventario/conteo', helpConteoStock],
  ['/inventario/lotes', helpLotesListado],
  ['/tesoreria/informes/cheques-terceros-cartera', helpInformeCheques],
  ['/tesoreria/informes/cheques-tercero', helpInformeCheques],
  ['/tesoreria/informes/cheques-propios-pendientes-acreditar', helpInformeCheques],
  ['/tesoreria/informes/cheques-propios', helpInformeCheques],
  ['/tesoreria/informes/movimientos-carteras', helpMovimientosCarterasInforme],
  ['/tesoreria/informes/personalizados', helpInformesPersonalizados],
  ['/tesoreria/informes/personalizado', helpInformesPersonalizados],
  ['/tesoreria/informes', helpTesoreriaInformesHub],
  ['/tesoreria/recibos', helpTesoreriaRecibos],
  ['/tesoreria/devoluciones/nuevo', helpTesoreriaDevolucionesNuevo],
  ['/tesoreria/devoluciones', helpTesoreriaDevoluciones],
  ['/tesoreria/orden-pago', helpTesoreriaOrdenPago],
  ['/tesoreria/movimientos-varios', helpTesoreriaMovVarios],
  ['/tesoreria/movimientos-varios/nuevo', helpTesoreriaMovVariosNuevo],
  ['/tesoreria/movimientos-bancarios/nuevo', helpTesoreriaMovVariosNuevo],
  ['/tesoreria/movimientos-bancarios', helpTesoreriaMovBancarios],
  ['/tesoreria/conciliacion-bancaria', helpConciliacionBancaria],
  ['/tesoreria/bancos', helpTablasBancarias],
  ['/tesoreria/carteras', helpTablasBancarias],
  ['/tesoreria/grupos-mcp', helpTablasBancarias],
  ['/tesoreria/medios-mcp', helpMediosMcp],
  ['/tesoreria/tarjetas', helpTablasBancarias],
  ['/tesoreria/movimientos-caja', helpPlanillaCaja],
  ['/tesoreria/disponibilidades', helpDisponibilidades],
  ['/tesoreria/transferencias', helpTesoreriaTransferencias],
  ['/sistema/usuarios', helpSistemaUsuarios],
  ['/sistema/tipoformulario', helpSistemaTipoFormulario],
  ['/sistema/tipo-formulario', helpSistemaTipoFormulario],
  ['/sistema/provincias', helpSistemaGeografia],
  ['/sistema/localidades', helpSistemaGeografia],
  ['/sistema/monedas', helpSistemaTiposMoneda],
  ['/sistema/monedas/nuevo', helpSistemaTiposMoneda],
  ['/sistema/movmoneda', helpSistemaMonedas],
  ['/sistema/movmoneda/nuevo', helpSistemaMonedas],
  ['/sistema/companias', helpSistemaCompanias],
  ['/sistema/certificados-arca', helpSistemaCertificadosArca],
  ['/sistema/wsfe-tipcomp', helpSistemaWsfeTipComp],
  ['/sistema/caja-usuario', helpSistemaCajaUsuario],
  ['/sistema/grupo-caja-usuario', helpSistemaCajaUsuario],
  ['/configuraciones/transportes', helpConfigTransportes],
  ['/configuraciones/impuestos', helpConfigImpuestos],
  ['/configuraciones/parametros', helpConfigParametros],
  ['/configuraciones/ubicaciones', helpConfigUbicaciones],
  ['/configuraciones/sucventa', helpConfigSucursales],
  ['/configuraciones/sucursalesventa', helpConfigSucursales],
  ['/configuraciones/tokens-integracion', helpConfigTokens],
  ['/configuraciones/claves-agente', helpConfigClavesAgente],
  ['/configuraciones/passwords', helpConfigPasswords],
  ['/configuraciones/webhooks/ayuda', helpConfigWebhooksDocs],
  ['/configuraciones/webhooks', helpConfigWebhooks],
  ['/contabilidad/asientos-diario', helpContabilidadLibroDiario],
  ['/contabilidad/libro-mayor', helpContabilidadLibroMayor],
  ['/contabilidad/ajuste-inflacion', helpAjusteInflacion],
  ['/contabilidad/plan-cuentas', helpContabilidadModulo],
  ['/contabilidad/informes/personalizados', helpInformesPersonalizados],
  ['/contabilidad/informes/personalizado', helpInformePersonalizadoContabilidad],
  ['/contabilidad/informes', helpContabilidadModulo],
  ['/contabilidad/estado-resultados', helpContabilidadLibroMayor],
  ['/contabilidad/situacion-patrimonial', helpContabilidadLibroMayor],
  ['/contabilidad/sumas-y-saldos', helpContabilidadSumasYSaldos],
  ['/contabilidad/imputables', helpContabilidadImputables],
  ['/contabilidad/noimputables', helpContabilidadNoImputables],
  ['/contabilidad/estrategia', helpContabilidadEstrategia],
  ['/sistema', helpSistemaModulo],
  ['/configuraciones', helpConfiguracionesModulo],
  ['/contabilidad', helpContabilidadModulo],
  ['/ventas', helpListadoTrabajoGenerico],
  ['/compras', helpListadoTrabajoGenerico],
  ['/inventario', helpListadoTrabajoGenerico],
  ['/tesoreria', helpListadoTrabajoGenerico],
];

export const HELP_PREFIX_SORTED: [string, PageHelpContent][] = [...PREFIX_ENTRIES].sort(
  (a, b) => b[0].length - a[0].length
);

export function resolveHelpFromCatalog(normalizedPath: string): PageHelpContent | null {
  for (const rule of HELP_SPECIAL_RULES) {
    if (rule.match(normalizedPath)) {
      return rule.content;
    }
  }
  const exact = HELP_EXACT[normalizedPath];
  if (exact) {
    return exact;
  }
  for (const [prefix, content] of HELP_PREFIX_SORTED) {
    if (normalizedPath === prefix || normalizedPath.startsWith(`${prefix}/`)) {
      return content;
    }
  }
  return null;
}
